import { Injectable } from '@angular/core';
import { formatNumber } from '@angular/common';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
dayjs.extend(isBetween);
import { catchError, concatMap, map, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';
import { RelocationType } from '../../enum/margin-calculator-reimbursement-type.enum';
import { MarginCalculatorClinicianService } from '../../services/margin-calculator-clinician.service';
import { MarginCalculatorContractInfoHelperService } from '../../services/margin-calculator-contract-info-helper.service';
import { MarginCalculatorDateService } from '../../services/margin-calculator-date.service';
import { MarginCalculatorFacilityService } from '../../services/margin-calculator-facility.service';
import {
    FORM_CONTROL_NAMES,
    FORM_NAMES,
    MarginCalculatorFormService
} from '../../services/margin-calculator-form.service';
import {
    InsuranceBenefitTypes,
    MarginCalculatorInsuranceService
} from '../../services/margin-calculator-insurance.service';
import { MarginCalculatorJobService } from '../../services/margin-calculator-job.service';
import { MarginCalculatorMarginService } from '../../services/margin-calculator-margin.service';
import { MarginCalculatorBillingService } from '../../services/margin-calculator-pay-billing.service';
import { MarginCalculatorReimbursementService } from '../../services/margin-calculator-reimbursement.service';
import { MarginCalculatorSpecialtyService } from '../../services/margin-calculator-specialty.service';
import {
    MarginCalculatorApiActions,
    MarginCalculatorComputationActions,
    MarginCalculatorPageActions,
    ReimbursementsActions,
    PayBillingActions,
    MarginCalculatorUserInterfaceActions,
    ReviewAndSendActions,
    DuplicateMarginActions,
    PipelineMarginActions,
    UpdateMarginActions,
    InsuranceActions
} from '../actions';
import * as validationsActions from 'src/app/root-store/validations/validations-actions';
import * as Selectors from '../selectors';
import { MarginCalculatorFormulaService } from '../../services/margin-calculator-formula.service';
import { MarginCalculatorStipendsService } from '../../services/margin-calculator-stipends.service';
import { MarginCalculatorInsuranceHelperService } from '../../services/margin-calculator-insurance-helper.service';
import { FacilityHelperService } from '../../services/facility-helper.service';
import { HousingActions } from '../actions/housing.actions';
import { HousingStateHelpers } from '../state/housing-state-helpers';
import { MarginCalculatorHousingService } from '../../services/margin-calculator-housing.service';
import {
    CERT_OTHER_ID,
    CERT_OTHER_NAME,
    EducationReimbursement,
    Facility,
    HOUSEWARE_OTHER_ID,
    Houseware,
    InsuranceRequest
} from '../../models';
import { isEmpty } from 'src/app/shared/utilities/obj-utilities';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';
import { FacilityMinimumWageService } from '../../services/facility-minimum-wage.service';
import { MatDialog } from '@angular/material/dialog';
import { MarginOfferLinkDialogComponent } from '../../dialogs/margin-offer-link-dialog.component';
import { MarginCalculatorAuthService } from '../../services/margin-calculator-auth.service';
import { ConfirmDialogService } from 'src/app/shared/services/confirm-dialog.service';
import { MarginCalculatorPayBillingHelperService } from '../../services/margin-calculator-pay-billing-helper.service';
import { PayBillingStateHelpers } from '../state/pay-billing-state-helper';
import { MinimumWageService } from '../../services/minimum-wage.service';
import { ContractStatusService } from 'src/app/facility-contracts/shared/contract-status.service';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { MarginCalculatorLogService } from '../../services/margin-calculator-log.service';
import { MarginValidationRule } from '../../enum/margin-validation-rule.enum';
import { MarginValidationLog } from '../../models/margin-validation-log.model';
import { MarginValidationLogDetail } from '../../models/margin-validation-log-detail.model';
import { PathfindersFlags } from 'src/app/shared/models/enums/team-feature-flags/pathfinders-feature-flags.enum';
import { ContractRequestService } from 'src/app/shared/lib/contract-request/contract-request.service';
import { maxRtoWithInsurance, maxRtoWithoutInsurance } from '../selectors';
import Big from 'big.js';
import { SharedOffersService } from 'src/app/shared/offers-shared/offers.service';

@Injectable()
export class MarginCalculatorEffects {
    loadMargin$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.loadMargin),
            concatMap(({ mcId }) =>
                this._marginService.getMargin(mcId).pipe(
                    map((margin) => MarginCalculatorApiActions.marginLoadedSuccess({ margin })),
                    catchError(() =>
                        of(MarginCalculatorApiActions.marginLoadedFail({ message: 'Failed to load margin' }))
                    )
                )
            )
        );
    });

    loadMarginsDifferences$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.loadMarginsDifferences),
            concatMap(({ previousMarginId, currentMarginId }) =>
                this._marginService.getMarginsDifferences(previousMarginId, currentMarginId).pipe(
                    map((marginsDifferences) =>
                        MarginCalculatorApiActions.marginsDifferencesLoadedSuccess({ marginsDifferences })
                    ),
                    catchError(() =>
                        of(
                            MarginCalculatorApiActions.marginsDifferencesLoadedFail({
                                message: 'Failed to load margins differences'
                            })
                        )
                    )
                )
            )
        );
    });

    handleLoadMarginClinician$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.setMargin, MarginCalculatorApiActions.marginLoadedSuccess),
            map(({ margin }) => MarginCalculatorPageActions.loadClinician({ clinicianId: margin.userId }))
        );
    });

    handleLoadMarginFacility$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorApiActions.marginLoadedSuccess),
            map(({ margin }) => MarginCalculatorPageActions.loadFacility({ facilityId: margin.detail.hospitalId }))
        );
    });

    loadClinician$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.loadClinician),
            concatMap(({ clinicianId }) =>
                this._marginClinicianService.getClinicianById(clinicianId).pipe(
                    map((clinician) => MarginCalculatorApiActions.clinicianLoadedSuccess({ clinician })),
                    catchError(() =>
                        of(MarginCalculatorApiActions.clinicianLoadedFail({ message: 'Failed to load clinician' }))
                    )
                )
            )
        );
    });

    loadClinicianNames$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.loadClinicianNames),
            switchMap(({ name }) =>
                this._marginClinicianService.getClinicianNames(10, name).pipe(
                    map((clinicians) => MarginCalculatorApiActions.clinicianNamesLoadedSuccess({ clinicians })),
                    catchError(() =>
                        of(
                            MarginCalculatorApiActions.clinicianNamesLoadedFail({
                                message: 'Failed to load clinician by name'
                            })
                        )
                    )
                )
            )
        );
    });

    loadClinicianDetailsAfterSelected$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.clinicianSelected),
            switchMap(({ clinician }) =>
                of(MarginCalculatorPageActions.loadClinician({ clinicianId: clinician.userId }))
            )
        );
    });

    handleUserInterfaceClinicianChanged$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.clinicianSelected),
            switchMap(() => {
                return of(
                    MarginCalculatorUserInterfaceActions.setUserInterfaceInitialClinicianChanged({
                        isInitialClinicianChanged: true
                    })
                );
            })
        );
    });

    loadClinicianEmails$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.loadClinicianEmails),
            switchMap(({ email }) =>
                this._marginClinicianService.getClinicianEmails(10, email).pipe(
                    map((clinicians) => MarginCalculatorApiActions.clinicianEmailsLoadedSuccess({ clinicians })),
                    catchError(() =>
                        of(
                            MarginCalculatorApiActions.clinicianEmailsLoadedFail({
                                message: 'Failed to load clinician by email'
                            })
                        )
                    )
                )
            )
        );
    });

    loadFacilityByNameSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorApiActions.facilityLoadedByNameSuccess),
            switchMap((payload) =>
                of(
                    MarginCalculatorApiActions.facilityLoadedSuccess({
                        facility: payload.facility,
                        dirtyForm: this._marginFormService.parentForm.dirty
                    })
                )
            )
        );
    });

    loadFacilityDetailsAfterSelected$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.facilitySelected),
            switchMap((payload) => of(MarginCalculatorPageActions.loadFacility(payload)))
        );
    });

    setUiFacilityChangedFlag$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.facilitySelected),
            switchMap(() =>
                of(
                    MarginCalculatorUserInterfaceActions.setUserInterfaceInitialFacilityChanged({
                        isInitialFacilityChanged: true
                    })
                )
            )
        );
    });

    loadFacility$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.loadFacility),
            switchMap(({ facilityId }) =>
                this._marginCalculatorFacilityService.getFacilityInfo(facilityId).pipe(
                    map((facility) => {
                        facility.discountPercent = facility.discountPercent ? facility.discountPercent * 100 : 0;
                        return MarginCalculatorApiActions.facilityLoadedSuccess({
                            facility,
                            dirtyForm: this._marginFormService.parentForm.dirty
                        });
                    }),
                    catchError(() =>
                        of(MarginCalculatorApiActions.facilityLoadedFail({ message: 'Failed to load facility' }))
                    )
                )
            )
        );
    });

    loadFacilities$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.loadFacilities),
            switchMap(({ name }) =>
                this._marginCalculatorFacilityService.getFacilities(name, 20).pipe(
                    map((facilities) => MarginCalculatorApiActions.facilitiesLoadedSuccess({ facilities })),
                    catchError(() =>
                        of(MarginCalculatorApiActions.facilitiesLoadedFail({ message: 'Failed to load facilities' }))
                    )
                )
            )
        );
    });

    facilityNameControlBlur$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.facilityNameControlBlur),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectFacilityName),
                this.store.select(Selectors.selectLoadFacilityStatusInProgress)
            ]),
            switchMap(([payload, currentFacilityName, facilityLoading]) => {
                if (facilityLoading) {
                    return of(MarginCalculatorPageActions.facilityLoadingInProgress());
                }

                if (payload?.name?.trim().toLowerCase() !== currentFacilityName?.trim().toLowerCase()) {
                    return of(MarginCalculatorPageActions.loadFacilityByName(payload));
                }

                return of(MarginCalculatorPageActions.facilityNameIsSame(payload));
            })
        );
    });

    loadFacilityByName$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.loadFacilityByName),
            switchMap(({ name }) =>
                this._marginCalculatorFacilityService.getFacilities(name, 1, true).pipe(
                    map((facilities) => {
                        if (facilities && facilities.length > 0) {
                            return MarginCalculatorApiActions.facilityLoadedByNameSuccess({
                                facility: facilities[0] as Facility
                            });
                        }
                        return MarginCalculatorApiActions.findFacilityByNameFail();
                    }),
                    catchError(() =>
                        of(MarginCalculatorApiActions.facilitiesLoadedFail({ message: 'Failed to load facilities' }))
                    ),
                    takeUntil(this.actions$.pipe(ofType(MarginCalculatorPageActions.facilitySelected)))
                )
            )
        );
    });

    loadBillingRates$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.loadBillingRates),
            switchMap(({ facilityId, professionId, specialtyId, weeks }) =>
                this._marginBillingService.getBillingRates(facilityId, professionId, specialtyId, weeks, false).pipe(
                    map((billingRates) => MarginCalculatorApiActions.billingRatesLoadedSuccess({ billingRates })),
                    catchError(() =>
                        of(
                            MarginCalculatorApiActions.billingRatesLoadedFail({
                                message: 'Failed to load billing rates'
                            })
                        )
                    )
                )
            )
        );
    });

    loadJob$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.loadJob),
            concatMap(({ jobId, nurseId }) =>
                this._marginJobService.getJob(jobId, nurseId).pipe(
                    map((job) => MarginCalculatorApiActions.jobLoadedSuccess({ job })),
                    catchError(() => of(MarginCalculatorApiActions.jobLoadedFail({ message: 'Failed to load job' })))
                )
            )
        );
    });

    handleLoadJobClinician$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorApiActions.jobLoadedSuccess),
            map(({ job }) => {
                return job.candidateId
                    ? MarginCalculatorPageActions.loadClinician({ clinicianId: job.candidateId })
                    : MarginCalculatorPageActions.noOp({ debug: 'handleLoadJobClinician$ effect' });
            })
        );
    });

    handleLoadJobSpecialty$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorApiActions.jobLoadedSuccess),
            map(({ job }) => {
                return job.expertiseId
                    ? MarginCalculatorPageActions.loadSpecialty({
                          professionId: job.professionId,
                          expertiseId: job.expertiseId
                      })
                    : MarginCalculatorPageActions.noOp({ debug: 'handleLoadJobSpecialty$ effect' });
            })
        );
    });

    grabEndDateForJobAfterLoaded$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorApiActions.jobLoadedSuccess),
            concatMap(({ job }) => {
                return this._marginDateService
                    .getContractEndDate(new Date(job.startDate).toDateString(), job.duration)
                    .pipe(
                        map((endDate) =>
                            MarginCalculatorApiActions.getContractEndDateLoadedSuccess({
                                endDate: endDate.toLocaleDateString()
                            })
                        ),
                        catchError(() =>
                            of(
                                MarginCalculatorApiActions.getContractEndDateLoadedFail({
                                    message: 'Failed to load end date after grabbing start date and weeks from job'
                                })
                            )
                        )
                    );
            })
        );
    });

    loadSpecialty$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.loadSpecialty),
            concatMap(({ professionId, expertiseId }) =>
                this._marginCalculatorSpecialtyService.getSpecialty(professionId, expertiseId).pipe(
                    map((specialty) => MarginCalculatorApiActions.specialtyLoadedSuccess({ specialty })),
                    catchError(() =>
                        of(MarginCalculatorApiActions.specialtyLoadedFail({ message: 'Failed to load specialty' }))
                    )
                )
            )
        );
    });

    loadReimbursements$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ReimbursementsActions.loadReimbursements),
            concatMap(() =>
                this._marginCalculatorReimbursementService.getReimbursements().pipe(
                    map((reimbursements) => {
                        const other: EducationReimbursement = {
                            id: CERT_OTHER_ID,
                            name: 'Other',
                            amount: 0
                        };
                        reimbursements.educationReimbursements.push(other);
                        return MarginCalculatorApiActions.reimbursementsLoadedSuccess({ reimbursements });
                    }),
                    catchError(() =>
                        of(
                            MarginCalculatorApiActions.reimbursementsLoadedFail({
                                message: 'Failed to load reimbursements'
                            })
                        )
                    )
                )
            )
        );
    });

    loadGsaRates$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorApiActions.loadGSARates),
            concatMap(({ facilityId, startDate, endDate }) =>
                this._marginClinicianService
                    .getGSALimits(facilityId, startDate.toDateString(), endDate.toDateString())
                    .pipe(
                        map((gsaRates) => MarginCalculatorApiActions.gsaRatesLoadedSuccess({ gsaRates })),
                        catchError(() =>
                            of(MarginCalculatorApiActions.gsaRatesLoadedFail({ message: 'Failed to load gsa rates' }))
                        )
                    )
            )
        );
    });

    loadEndDateAfterStartDateOrWeeksChange$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.setStartDate, MarginCalculatorPageActions.setWeeks),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectStartDateAsDate),
                this.store.select(Selectors.selectWeeks)
            ]),
            concatMap(([, startDate, weeks]) => {
                return this._marginDateService.getContractEndDate(startDate.toDateString(), weeks).pipe(
                    map((endDate) =>
                        MarginCalculatorApiActions.getContractEndDateLoadedSuccess({
                            endDate: endDate.toLocaleDateString()
                        })
                    ),
                    catchError(() =>
                        of(
                            MarginCalculatorApiActions.getContractEndDateLoadedFail({
                                message: 'Failed to load end date after start date or contract length was changed'
                            })
                        )
                    )
                );
            })
        );
    });

    loadUserCredentials$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.marginCalculatorComponentLoaded),
            concatMap(() => {
                return this._marginCalculatorAuthService.getUserCredentials().pipe(
                    map((userCredentials) =>
                        MarginCalculatorApiActions.userCredentialsLoadedSuccess({ userCredentials })
                    ),
                    catchError(() =>
                        of(
                            MarginCalculatorApiActions.userCredentialsLoadedFail({
                                message: 'Failed to load user credentials'
                            })
                        )
                    )
                );
            })
        );
    });

    loadEndDateForNewMargin$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.marginCalculatorComponentLoaded),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectStartDateAsDate),
                this.store.select(Selectors.selectWeeks),
                this.store.select(Selectors.selectMarginExists)
            ]),
            concatMap(([, startDate, weeks, marginExists]) => {
                if (!marginExists) {
                    return this._marginDateService.getContractEndDate(startDate.toDateString(), weeks).pipe(
                        map((endDate) =>
                            MarginCalculatorApiActions.getContractEndDateLoadedSuccess({
                                endDate: endDate.toLocaleDateString()
                            })
                        ),
                        catchError(() =>
                            of(
                                MarginCalculatorApiActions.getContractEndDateLoadedFail({
                                    message: 'Failed to load end date for a new margin'
                                })
                            )
                        )
                    );
                }

                return of(MarginCalculatorPageActions.noOp({ debug: 'loadEndDateForNewMargin$ effect' }));
            })
        );
    });

    loadEndDateAfterContractDaysChange$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.setContractDays),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectStartDateAsDate),
                this.store.select(Selectors.selectContractDays),
                this.store.select(Selectors.selectShifts)
            ]),
            concatMap(([, startDate, contractDays, shifts]) =>
                this._marginDateService.getContractDates(startDate.toDateString(), contractDays, shifts).pipe(
                    map(({ endDate }) =>
                        MarginCalculatorApiActions.getContractDatesLoadedSuccess({
                            endDate: endDate.toLocaleDateString()
                        })
                    ),
                    catchError(() =>
                        of(
                            MarginCalculatorApiActions.getContractDatesLoadedFail({
                                message: 'Failed to load end date after contract days were changed'
                            })
                        )
                    )
                )
            )
        );
    });

    setRelocationType$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ReimbursementsActions.setRelocationType),
            map(({ relocationType }) =>
                relocationType === RelocationType.unevenSplit
                    ? MarginCalculatorPageActions.noOp({ debug: 'setRelocationType$ effect' })
                    : ReimbursementsActions.setRelocationEnd({ relocationFrom: 0 })
            )
        );
    });

    licenseSelected$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ReimbursementsActions.licenseSelected),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectStateLicensures),
                this.store.select(Selectors.selectReimbursements)
            ]),
            switchMap(([{ code }, stateLicensures, reimbursements]) => {
                if (!stateLicensures) {
                    stateLicensures = [];
                }
                const exists = stateLicensures.some((item) => item.code === code);
                if (exists) {
                    return of(MarginCalculatorPageActions.noOp({ debug: 'licenseSelected$ effect' }));
                }
                const license = reimbursements.stateLicensureReimbursements.find((item) => item.code === code);
                const initialCost = license.updatedCost ?? license.initialCost;
                const licenses = [...stateLicensures, { ...license, initialCost, selected: true }];
                return of(ReimbursementsActions.setLicenses({ licenses }));
            })
        );
    });

    licenseUnselected$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ReimbursementsActions.licenseUnselected),
            concatLatestFrom(() => this.store.select(Selectors.selectStateLicensures)),
            concatMap(([{ code }, stateLicensures]) => {
                if (!stateLicensures) {
                    stateLicensures = [];
                }
                const exists = stateLicensures.some((item) => item.code === code);
                if (!exists) {
                    return of(MarginCalculatorPageActions.noOp({ debug: 'licenseUnselected$ effect' }));
                }
                const licenses = stateLicensures.filter((item) => item.code !== code);
                return of(ReimbursementsActions.setLicenses({ licenses }));
            })
        );
    });

    selectCertAfterAmountSet$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ReimbursementsActions.setCertificationAmount),
            switchMap(({ id }) => of(ReimbursementsActions.certificationSelected({ id })))
        );
    });

    certSelected$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ReimbursementsActions.certificationSelected),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectEducationReimbursements),
                this.store.select(Selectors.selectEducationReimbursementsFullList),
                this.store.select(Selectors.selectCertificationOtherValue),
                this.store.select(Selectors.selectCertificationOtherText)
            ]),
            switchMap(([{ id }, educationReimbursements, educationReimbursementsFullList, otherValue, otherText]) => {
                if (!educationReimbursements) {
                    educationReimbursements = [];
                }
                const certifications = educationReimbursements.filter((item) => item.id !== id);
                let cert = educationReimbursementsFullList.find((item) => item.id === id);
                if (id === CERT_OTHER_ID) {
                    cert = {
                        id: CERT_OTHER_ID,
                        name: otherText || CERT_OTHER_NAME,
                        amount: otherValue
                    };
                }
                certifications.push(cert);
                return of(ReimbursementsActions.setCertifications({ certifications }));
            })
        );
    });

    certUnselected$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ReimbursementsActions.certificationUnselected),
            concatLatestFrom(() => this.store.select(Selectors.selectEducationReimbursements)),
            concatMap(([{ id }, educationReimbursements]) => {
                if (!educationReimbursements) {
                    educationReimbursements = [];
                }
                const exists = educationReimbursements.some((item) => item.id === id);
                if (!exists) {
                    return of(MarginCalculatorPageActions.noOp({ debug: 'certUnselected$ effect' }));
                }
                const certifications = educationReimbursements.filter((item) => item.id !== id);
                return of(ReimbursementsActions.setCertifications({ certifications }));
            })
        );
    });

    setLicenseAmount$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ReimbursementsActions.setLicenseAmount),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectStateLicensures),
                this.store.select(Selectors.selectReimbursements)
            ]),
            switchMap(([{ code, amount }, licenses, reimbursements]) => {
                if (!licenses) {
                    licenses = [];
                }
                const exists = licenses.some((item) => item.code === code);
                if (exists) {
                    return of(
                        ReimbursementsActions.setLicenses({
                            licenses: licenses.map((license) =>
                                license.code === code ? { ...license, initialCost: amount } : license
                            )
                        })
                    );
                }

                const newLicense = reimbursements.stateLicensureReimbursements.find((item) => item.code === code);
                return of(
                    ReimbursementsActions.setLicenses({
                        licenses: [...licenses, { ...newLicense, initialCost: amount }]
                    })
                );
            })
        );
    });

    loadError$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MarginCalculatorApiActions.marginLoadedFail,
                MarginCalculatorApiActions.clinicianLoadedFail,
                MarginCalculatorApiActions.clinicianNamesLoadedFail,
                MarginCalculatorApiActions.clinicianEmailsLoadedFail,
                MarginCalculatorApiActions.facilityLoadedFail,
                MarginCalculatorApiActions.facilitiesLoadedFail,
                MarginCalculatorApiActions.jobLoadedFail,
                MarginCalculatorApiActions.reimbursementsLoadedFail,
                MarginCalculatorApiActions.gsaRatesLoadedFail,
                MarginCalculatorApiActions.getContractDatesLoadedFail,
                MarginCalculatorApiActions.getContractEndDateLoadedFail,
                MarginCalculatorApiActions.getContractLengthWithBookingWeeksLoadedFail,
                MarginCalculatorApiActions.moneyMovementValidationsLoadedFail,
                MarginCalculatorApiActions.offerAyaPaidBonusLoadedFail,
                ReviewAndSendActions.createContractRequestFail,
                DuplicateMarginActions.duplicateMarginFail,
                PipelineMarginActions.pipelineMarginFail,
                ReviewAndSendActions.reviewAndSendMarginFail,
                ReviewAndSendActions.reviewAndSendMarginBlockedByViolations,
                UpdateMarginActions.saveMarginFail
            ),
            map(({ message }) => showToasterMessage({ message }))
        );
    });

    checkForSettingBillableHours$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MarginCalculatorApiActions.getContractDatesLoadedSuccess,
                MarginCalculatorApiActions.getContractEndDateLoadedSuccess,
                MarginCalculatorApiActions.getContractLengthWithBookingWeeksLoadedSuccess,
                MarginCalculatorApiActions.billingRatesLoadedSuccess,
                MarginCalculatorApiActions.marginLoadedSuccess,
                MarginCalculatorPageActions.setEndDate,
                MarginCalculatorPageActions.setVacationDates,
                MarginCalculatorPageActions.setShiftType,
                MarginCalculatorPageActions.setShifts,
                MarginCalculatorPageActions.setAltShifts,
                MarginCalculatorPageActions.setAltShiftHours,
                MarginCalculatorPageActions.marginCalculatorComponentLoaded,
                PayBillingActions.overtimeRulesChanged,
                MarginCalculatorPageActions.setIsExtension,
                MarginCalculatorPageActions.setIsPendingFacilityApproval
            ),
            concatLatestFrom(() => [this.store.select(Selectors.selectSetBillableHoursParams)]),
            concatMap(([, selectParams]) => {
                const {
                    billingRegHours,
                    billingOtHours,
                    primaryNonStandardOvertimeBillingHours,
                    alternateNonStandardOvertimeBillingHours
                } = this._marginContractInfoHelperService.setBillableHours(
                    selectParams.shiftType,
                    selectParams.shiftHours,
                    selectParams.altShiftHours,
                    selectParams.shifts,
                    selectParams.altShifts,
                    selectParams.bookingWeeks,
                    selectParams.altBookingWeeks,
                    selectParams.startDate,
                    selectParams.endDate,
                    selectParams.pendingApproval ? [] : selectParams.vacationDates,
                    selectParams.otBillingRulesEnabled,
                    selectParams.otFirstHour,
                    selectParams.otSecondHour,
                    selectParams.otFirstFrequency,
                    selectParams.otSecondFrequency,
                    selectParams.otAndOr,
                    selectParams.orientationHours,
                    selectParams.billingRates
                );

                return of(
                    MarginCalculatorComputationActions.billableHoursComputedSuccess({
                        billingRegHours,
                        billingOtHours,
                        primaryNonStandardOvertimeBillingHours,
                        alternateNonStandardOvertimeBillingHours
                    })
                );
            })
        );
    });

    checkShouldOvertimeBillingRuleBeEnabled$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MarginCalculatorPageActions.setShiftType,
                MarginCalculatorPageActions.setShifts,
                MarginCalculatorPageActions.setAltShifts,
                MarginCalculatorPageActions.setShiftHours,
                MarginCalculatorPageActions.setAltShiftHours,
                MarginCalculatorApiActions.marginLoadedSuccess,
                MarginCalculatorApiActions.jobLoadedSuccess,
                MarginCalculatorApiActions.billingRatesLoadedSuccess,
                MarginCalculatorApiActions.specialtyLoadedSuccess,
                MarginCalculatorPageActions.specialtyIdSelected,
                MarginCalculatorApiActions.clinicianEmailsLoadedSuccess
            ),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectExpectedBillingOvertimeEnabled),
                this.store.select(Selectors.selectIsMarginCreatedFromJob)
            ]),
            concatMap(([, isBillOverTime, isMarginCreatedFromJob]) => {
                if (this._marginFormService.parentForm.dirty || isMarginCreatedFromJob) {
                    return of(
                        MarginCalculatorComputationActions.billingOvertimeEnabledComputedSuccess({ isBillOverTime })
                    );
                }

                return of(
                    MarginCalculatorPageActions.noOp({ debug: 'checkShouldOvertimeBillingRuleBeEnabled$ effect' })
                );
            })
        );
    });

    loadContractLengthAfterShiftTypeDatesOrNumberOfShiftsChange$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MarginCalculatorPageActions.marginCalculatorComponentLoaded,
                MarginCalculatorPageActions.setShiftType,
                MarginCalculatorPageActions.setEndDate,
                MarginCalculatorPageActions.setShifts,
                MarginCalculatorPageActions.setVacationDates,
                MarginCalculatorApiActions.marginLoadedSuccess,
                MarginCalculatorApiActions.getContractEndDateLoadedSuccess,
                MarginCalculatorApiActions.getContractDatesLoadedSuccess,
                MarginCalculatorPageActions.setIsPendingFacilityApproval
            ),
            concatLatestFrom(() => [this.store.select(Selectors.selectGetContractLengthParams)]),
            concatMap(([, selectParams]) =>
                this._marginContractInfoHelperService
                    .getContractLengthWithBookingWeeks(
                        selectParams.shiftType,
                        selectParams.startDate,
                        selectParams.endDate,
                        selectParams.pendingApproval ? [] : selectParams.vacationDates
                    )
                    .pipe(
                        map((result) =>
                            MarginCalculatorApiActions.getContractLengthWithBookingWeeksLoadedSuccess({
                                weeks: result.contractLength,
                                lastWeeksLength: result.contractLength,
                                bookingWeeks: result.bookingWeeks,
                                altBookingWeeks: result.altBookingWeeks
                            })
                        ),
                        catchError(() =>
                            of(
                                MarginCalculatorApiActions.getContractLengthWithBookingWeeksLoadedFail({
                                    message: 'Failed to load contract length with booking weeks'
                                })
                            )
                        )
                    )
            )
        );
    });

    setPayHours$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MarginCalculatorApiActions.getContractLengthWithBookingWeeksLoadedSuccess,
                MarginCalculatorApiActions.facilityLoadedSuccess,
                MarginCalculatorApiActions.marginLoadedSuccess,
                MarginCalculatorPageActions.setAltShifts,
                MarginCalculatorPageActions.setShiftHours,
                MarginCalculatorPageActions.setAltShiftHours
            ),
            concatLatestFrom(() => [this.store.select(Selectors.selectPayHoursParams)]),
            concatMap(([, selectParams]) => {
                const { regPayHrs, otPayHrs, altRegPayHrs, altOtPayHrs } =
                    this._marginContractInfoHelperService.setPayHours(
                        selectParams.shiftType,
                        selectParams.facilityState,
                        selectParams.shifts,
                        selectParams.shiftHours,
                        selectParams.bookingWeeks,
                        selectParams.altBookingWeeks,
                        selectParams.altShifts,
                        selectParams.altShiftHours
                    );

                return of(
                    MarginCalculatorComputationActions.payHoursComputedSuccess({
                        regPayHrs,
                        otPayHrs,
                        altRegPayHrs,
                        altOtPayHrs
                    })
                );
            })
        );
    });

    checkForSettingBaseBillingRate$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MarginCalculatorApiActions.billingRatesLoadedSuccess,
                MarginCalculatorApiActions.marginLoadedSuccess,
                MarginCalculatorApiActions.jobLoadedSuccess,
                MarginCalculatorPageActions.setShiftTime,
                MarginCalculatorPageActions.marginCalculatorComponentLoaded,
                MarginCalculatorPageActions.setStartTimeFrameHours,
                MarginCalculatorPageActions.setStartTimeFrameMinutes
            ),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectSetBaseBillingRateParams),
                this.store.select(Selectors.selectMarginExists),
                this.store.select(Selectors.selectUserInterfaceIsInitialSpecialtyChanged),
                this.store.select(Selectors.selectSpecialtyId)
            ]),
            switchMap(([, selectParams, marginExists, isInitialSpecialtyChanged, specialtyId]) => {
                const baseBillingRate =
                    specialtyId > 0
                        ? this._marginContractInfoHelperService.setBaseBillingRate(
                              false,
                              selectParams.shiftHours,
                              selectParams.shiftTime,
                              selectParams.marginBillRate,
                              selectParams.job,
                              selectParams.billingRates,
                              marginExists,
                              isInitialSpecialtyChanged
                          )
                        : null;

                return of(MarginCalculatorComputationActions.baseBillingRateComputedSuccess({ baseBillingRate }));
            })
        );
    });

    checkForSettingBaseBillingRateAlt$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MarginCalculatorApiActions.billingRatesLoadedSuccess,
                MarginCalculatorApiActions.marginLoadedSuccess,
                MarginCalculatorApiActions.jobLoadedSuccess,
                MarginCalculatorPageActions.setAltShiftTime,
                MarginCalculatorPageActions.setAltShiftHours,
                MarginCalculatorPageActions.marginCalculatorComponentLoaded,
                MarginCalculatorPageActions.setAltStartTimeFrameHours,
                MarginCalculatorPageActions.setShiftType
            ),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectSetAltBaseBillingRateParams),
                this.store.select(Selectors.selectSpecialtyId),
                this.store.select(Selectors.selectIsStandardShift),
                this.store.select(Selectors.selectFeatureFlags)
            ]),
            switchMap(([, selectParams, specialtyId, isStandardShift, featureFlags]) => {
                if (!isStandardShift || featureFlags[PathfindersFlags.StopAltBaseBillRateForStandardShifts] === false) {
                    const baseBillingRateAlt =
                        specialtyId > 0
                            ? this._marginContractInfoHelperService.setBaseBillingRate(
                                  true,
                                  selectParams.altShiftHours,
                                  selectParams.altShiftTime,
                                  selectParams.billRateAlt,
                                  selectParams.job,
                                  selectParams.billingRates
                              )
                            : null;

                    return of(
                        MarginCalculatorComputationActions.baseBillingRateAltComputedSuccess({ baseBillingRateAlt })
                    );
                }

                return of(MarginCalculatorComputationActions.resetBaseBillingRateAlt());
            })
        );
    });

    checkForAdjustingTimeFrame$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.setShiftTime),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectShiftTime),
                this.store.select(Selectors.selectShiftHours)
            ]),
            switchMap(([, shiftTime, shiftHours]) => {
                if (this._marginFormService.parentForm.dirty) {
                    const timeFrame = this._marginContractInfoHelperService.populateTimeFrame(shiftTime, shiftHours);
                    return of(
                        MarginCalculatorComputationActions.timeFrameComputedSuccess({
                            shiftH: timeFrame.startHours,
                            shiftM: timeFrame.startMinutes,
                            shiftToH: timeFrame.endHours,
                            shiftToM: timeFrame.endMinutes
                        })
                    );
                }

                return of(MarginCalculatorPageActions.noOp({ debug: 'checkForAdjustingTimeFrame$ effect' }));
            })
        );
    });

    checkForAdjustingAltTimeFrame$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.setAltShiftTime),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectAltShiftTime),
                this.store.select(Selectors.selectAltShiftHours)
            ]),
            switchMap(([, altShiftTime, altShiftHours]) => {
                if (this._marginFormService.parentForm.dirty) {
                    const timeFrame = this._marginContractInfoHelperService.populateTimeFrame(
                        altShiftTime,
                        altShiftHours
                    );
                    return of(
                        MarginCalculatorComputationActions.altTimeFrameComputedSuccess({
                            shiftAltH: timeFrame.startHours,
                            shiftAltM: timeFrame.startMinutes,
                            shiftAltToH: timeFrame.endHours,
                            shiftAltToM: timeFrame.endMinutes
                        })
                    );
                }

                return of(MarginCalculatorPageActions.noOp({ debug: 'checkForAdjustingAltTimeFrame$ effect' }));
            })
        );
    });

    checkForVacationFlagChange$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.vacationSelected),
            concatLatestFrom(() => [this.store.select(Selectors.selectIsVacation)]),
            switchMap(([, isVacation]) => {
                if (isVacation) {
                    return of(MarginCalculatorPageActions.revealVacationDetails());
                }

                const selectedElements = Array.from(document.getElementsByClassName('date-selected'));
                selectedElements.forEach((element) => {
                    element.classList.remove('date-selected');
                });

                return of(MarginCalculatorPageActions.setVacationDates({ dates: [] }));
            })
        );
    });

    checkForChangingStartOrEndDateAdjustingVacation$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MarginCalculatorApiActions.marginLoadedSuccess,
                MarginCalculatorApiActions.getContractEndDateLoadedSuccess,
                MarginCalculatorApiActions.getContractDatesLoadedSuccess,
                MarginCalculatorPageActions.setEndDate,
                MarginCalculatorPageActions.setStartDate
            ),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectStartDateAsDate),
                this.store.select(Selectors.selectEndDateAsDate),
                this.store.select(Selectors.selectVacationDatesAsDates)
            ]),
            switchMap(([, startDate, endDate, vacationDates]) => {
                const dates = vacationDates.filter((value) => {
                    return dayjs(value).isBetween(startDate, endDate, undefined, '[]');
                });

                if (dates.length !== vacationDates.length) {
                    if (dates.length === 0) {
                        this._toasterService.toast('Vacation removed due to date change.');
                    }
                    return of(MarginCalculatorPageActions.setVacationDates({ dates }));
                }

                return of(
                    MarginCalculatorPageActions.noOp({
                        debug: 'checkForChangingStartOrEndDateAdjustingVacation$ effect'
                    })
                );
            })
        );
    });

    checkForSettingEndTimeFrameHours$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.setStartTimeFrameHours),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectStartTimeFrameHours),
                this.store.select(Selectors.selectShiftHours)
            ]),
            switchMap(([, startTimeFrameHours, shiftHours]) => {
                const shiftToH = this._marginDateService.getEndHours(startTimeFrameHours, shiftHours);
                return of(MarginCalculatorComputationActions.endTimeFrameHoursComputedSuccess({ shiftToH }));
            })
        );
    });

    checkForSettingAltEndTimeFrameHours$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.setAltStartTimeFrameHours),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectAltStartTimeFrameHours),
                this.store.select(Selectors.selectAltShiftHours)
            ]),
            switchMap(([, altStartTimeFrameHours, altShiftHours]) => {
                const shiftAltToH = this._marginDateService.getEndHours(altStartTimeFrameHours, altShiftHours);
                return of(MarginCalculatorComputationActions.altEndTimeFrameHoursComputedSuccess({ shiftAltToH }));
            })
        );
    });

    checkForSettingContractDaysAfterStartOrEndDatesChange$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MarginCalculatorApiActions.marginLoadedSuccess,
                MarginCalculatorApiActions.getContractEndDateLoadedSuccess,
                MarginCalculatorPageActions.setEndDate,
                MarginCalculatorPageActions.marginCalculatorComponentLoaded
            ),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectStartDateAsDate),
                this.store.select(Selectors.selectEndDateAsDate)
            ]),
            switchMap(([, startDate, endDate]) => {
                const contractDays = this._marginDateService.getDateDiffInDays(startDate, endDate);
                return of(
                    MarginCalculatorComputationActions.contractDaysComputedSuccess({
                        contractDays,
                        lastDaysLength: contractDays
                    })
                );
            })
        );
    });

    checkForLoadGsaRates$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MarginCalculatorApiActions.marginLoadedSuccess,
                MarginCalculatorApiActions.facilityLoadedSuccess,
                MarginCalculatorApiActions.getContractEndDateLoadedSuccess,
                MarginCalculatorApiActions.getContractDatesLoadedSuccess,
                MarginCalculatorPageActions.setStartDate,
                MarginCalculatorPageActions.setEndDate
            ),
            concatLatestFrom(() => [this.store.select(Selectors.selectGsaRateParams)]),
            switchMap(([, selectParams]) => {
                if (selectParams.facilityId) {
                    return of(
                        MarginCalculatorApiActions.loadGSARates({
                            facilityId: selectParams.facilityId,
                            startDate: selectParams.startDate,
                            endDate: selectParams.endDate
                        })
                    );
                }

                return of(MarginCalculatorPageActions.noOp({ debug: 'checkForLoadGsaRates$ effect' }));
            })
        );
    });

    checkForWeeklyHours$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MarginCalculatorApiActions.marginLoadedSuccess,
                MarginCalculatorApiActions.jobLoadedSuccess,
                MarginCalculatorPageActions.setShifts,
                MarginCalculatorPageActions.setShiftHours,
                MarginCalculatorPageActions.marginCalculatorComponentLoaded
            ),
            concatLatestFrom(() => [this.store.select(Selectors.selectWeeklyHoursParams)]),
            switchMap(([, selectParams]) => {
                const weeklyHours = selectParams.shifts * selectParams.shiftHours;
                return of(MarginCalculatorComputationActions.weeklyHoursComputedSuccess({ weeklyHours }));
            })
        );
    });

    checkForShiftHoursChange$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.setShiftHours),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectShiftHours),
                this.store.select(Selectors.selectEndTimeFrameMinutes),
                this.store.select(Selectors.selectStartTimeFrameHours)
            ]),
            switchMap(([, shiftHours, endTimeFrameMinutes, startTimeFrameHours]) => {
                let minutes = this._marginDateService.getEndMinutes(endTimeFrameMinutes, shiftHours);
                let hours = this._marginDateService.getEndHours(startTimeFrameHours, shiftHours);

                if (minutes >= 60) {
                    hours += 1;
                    minutes = 0;
                }

                return of(
                    MarginCalculatorComputationActions.endTimeFrameComputedSuccess({
                        shiftToH: hours,
                        shiftToM: minutes
                    })
                );
            })
        );
    });

    checkForAltShiftHoursChange$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.setAltShiftHours),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectAltShiftHours),
                this.store.select(Selectors.selectAltEndTimeFrameMinutes),
                this.store.select(Selectors.selectAltStartTimeFrameHours)
            ]),
            switchMap(([, altShiftHours, altEndTimeFrameMinutes, altStartTimeFrameHours]) => {
                let minutes = this._marginDateService.getEndMinutes(altEndTimeFrameMinutes, altShiftHours);
                let hours = this._marginDateService.getEndHours(altStartTimeFrameHours, altShiftHours);

                if (minutes >= 60) {
                    hours += 1;
                    minutes = 0;
                }

                return of(
                    MarginCalculatorComputationActions.altEndTimeFrameComputedSuccess({
                        shiftAltToH: hours,
                        shiftAltToM: minutes
                    })
                );
            })
        );
    });

    setHalfwayDate$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MarginCalculatorApiActions.marginLoadedSuccess,
                MarginCalculatorApiActions.getContractEndDateLoadedSuccess,
                MarginCalculatorApiActions.getContractDatesLoadedSuccess,
                MarginCalculatorPageActions.setStartDate,
                MarginCalculatorPageActions.setEndDate,
                MarginCalculatorPageActions.marginCalculatorComponentLoaded
            ),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectStartDateAsDate),
                this.store.select(Selectors.selectEndDateAsDate)
            ]),
            switchMap(([, startDate, endDate]) => {
                const dayDif = Math.floor(dayjs(endDate).diff(startDate, 'day') / 2);
                const halfwayDate = dayjs(startDate).add(dayDif, 'day').toDate().toLocaleDateString();

                return of(MarginCalculatorComputationActions.halfwayDateComputedSuccess({ halfwayDate }));
            })
        );
    });

    handleLoadInsuranceInfo$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MarginCalculatorApiActions.facilityLoadedSuccess,
                MarginCalculatorApiActions.clinicianEmailsLoadedSuccess,
                MarginCalculatorPageActions.setShiftHours,
                MarginCalculatorPageActions.setShifts,
                MarginCalculatorPageActions.setEndDate,
                MarginCalculatorApiActions.getContractDatesLoadedSuccess,
                MarginCalculatorApiActions.getContractEndDateLoadedSuccess,
                MarginCalculatorPageActions.setInsuranceNeeded,
                MarginCalculatorPageActions.setIneligibilityDialog,
                MarginCalculatorPageActions.setIsExtendedBenefits
            ),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectClinicianId),
                this.store.select(Selectors.selectStartDateAsDate),
                this.store.select(Selectors.selectEndDateAsDate),
                this.store.select(Selectors.selectTotalHoursPerWeek),
                this.store.select(Selectors.selectFacilityId),
                this.store.select(Selectors.selectOverrideIneligibility),
                this.store.select(Selectors.selectIsExtendedBenefits),
                this.store.select(Selectors.selectNeedInsurance),
                this.store.select(Selectors.selectRequireOverride)
            ]),
            switchMap(
                ([
                    action,
                    nurseId,
                    startDate,
                    endDate,
                    weeklyHours,
                    facilityId,
                    overridden,
                    extendBenefits,
                    needInsurance,
                    overrideRequired
                ]) => {
                    if (
                        overrideRequired &&
                        needInsurance &&
                        action.type === MarginCalculatorPageActions.setInsuranceNeeded.type
                    ) {
                        return of(
                            MarginCalculatorPageActions.showOverrideInsuranceDialog({
                                insuranceSelectionRecentTrigger: action.type
                            })
                        );
                    }

                    if (!needInsurance && action.type === MarginCalculatorPageActions.setInsuranceNeeded.type) {
                        return of(
                            MarginCalculatorPageActions.noOp({
                                debug: 'handleLoadInsuranceInfo$ effect - setInsuranceNeeded to false, do not load info'
                            })
                        );
                    }

                    const overrideIneligibility =
                        action.type === MarginCalculatorPageActions.setIsExtendedBenefits.type ||
                        action.type === MarginCalculatorPageActions.setIneligibilityDialog.type
                            ? overridden
                            : false;

                    const insuranceRequest: InsuranceRequest = {
                        nurseId,
                        startDate,
                        endDate,
                        weeklyHours,
                        facilityId,
                        overrideIneligibility
                    };

                    if (
                        action.type === MarginCalculatorPageActions.setIsExtendedBenefits.type ||
                        (!this._marginFormService?.parentForm?.dirty && needInsurance)
                    ) {
                        insuranceRequest.extendBenefits = extendBenefits;
                    }

                    return of(
                        MarginCalculatorPageActions.loadInsuranceInfo({
                            insuranceRequest,
                            insuranceSelectionRecentTrigger: action.type
                        })
                    );
                }
            )
        );
    });

    showOverrideInsuranceDialog$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.showOverrideInsuranceDialog),
            concatLatestFrom(() => [this.store.select(Selectors.selectInsuranceOverrideDialogSettings)]),
            switchMap(([, settings]) => {
                return this._confirmDialogService.showDialog(settings).pipe(
                    switchMap((isConfirmed) => {
                        if (isConfirmed) {
                            return of(
                                MarginCalculatorPageActions.setIneligibilityDialog({
                                    overrideIneligibility: true,
                                    isBenefit: true
                                })
                            );
                        }

                        return of(
                            MarginCalculatorPageActions.setIneligibilityDialog({
                                overrideIneligibility: false,
                                isBenefit: false
                            })
                        );
                    })
                );
            })
        );
    });

    loadInsuranceInfo$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.loadInsuranceInfo),
            concatMap(({ insuranceRequest }) =>
                this._marginInsuranceService.getBenefitsInfo(insuranceRequest).pipe(
                    map((insuranceInfo) => MarginCalculatorApiActions.insuranceInfoLoadedSuccess({ insuranceInfo })),
                    catchError(() =>
                        of(
                            MarginCalculatorApiActions.insuranceInfoLoadedFail({
                                message: 'Failed to load insurance info'
                            })
                        )
                    )
                )
            )
        );
    });

    handleInsuranceOverride$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorApiActions.insuranceInfoLoadedSuccess),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectInsuranceInfo),
                this.store.select(Selectors.selectOverrideIneligibility),
                this.store.select(Selectors.selectInsuranceSelectionRecentTrigger),
                this.store.select(Selectors.selectNeedInsurance)
            ]),
            switchMap(([, insuranceInfo, overridden, triggeredAction, isBenefit]) => {
                const overrideIneligibility =
                    (!this._marginFormService.parentForm.dirty && isBenefit) ||
                    (triggeredAction === MarginCalculatorPageActions.setIsExtendedBenefits.type ||
                    triggeredAction === MarginCalculatorPageActions.setIneligibilityDialog.type
                        ? overridden
                        : false);
                let requireOverride = false;
                const insuranceBenefitTypes = insuranceInfo.insuranceBenefitTypes;
                if (
                    insuranceInfo &&
                    insuranceInfo.insuranceBenefitTypes === (InsuranceBenefitTypes.Ineligible as number)
                ) {
                    requireOverride = true;
                } else {
                    requireOverride = false;
                }

                return of(
                    MarginCalculatorPageActions.setInsuranceOverrideOption({
                        overrideIneligibility,
                        requireOverride,
                        insuranceBenefitTypes
                    })
                );
            })
        );
    });

    handleRevealInsuranceDetails$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.setInsuranceNeeded, MarginCalculatorPageActions.setIneligibilityDialog),
            concatLatestFrom(() => [this.store.select(Selectors.selectShowInsuranceDetails)]),
            switchMap(([, showInsurance]) => {
                if (showInsurance) {
                    return of(InsuranceActions.revealInsuranceDetailsClicked());
                }

                return of(MarginCalculatorPageActions.clearMarginMedicalAndDentalInfo());
            })
        );
    });

    calculateStipendsPerContract$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MarginCalculatorApiActions.getContractLengthWithBookingWeeksLoadedSuccess,
                MarginCalculatorPageActions.setBookingWeeks,
                PayBillingActions.setCalculatedStipendValues,
                PayBillingActions.setHousingStipend,
                PayBillingActions.setHousingStipendAndRelatedValues,
                PayBillingActions.setMealStipend,
                PayBillingActions.setMealStipendAndRelatedValues,
                PayBillingActions.setCalculatedStipendValues
            ),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectMealStipend),
                this.store.select(Selectors.selectHousingStipend),
                this.store.select(Selectors.selectTotalBookingWeeks)
            ]),
            switchMap(([, mealStipend, housingStipend, totalBookingWeeks]) => {
                const stipendsPerContract = this._formulaService.calculateStipendsPerContract(
                    mealStipend,
                    housingStipend,
                    totalBookingWeeks
                );
                return of(PayBillingActions.setStipendsPerContract({ stipendsPerContract }));
            })
        );
    });

    calculateMaxMealsStipend$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorApiActions.gsaRatesLoadedSuccess),
            concatLatestFrom(() => [this.store.select(Selectors.selectGsaRates)]),
            switchMap(([, gsaRates]) => {
                const maxMeals = this._stipendService.getMaxMeals(gsaRates);
                return of(PayBillingActions.setMaxMeal({ maxMeals }));
            })
        );
    });

    calculateMaxHousingStipend$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorApiActions.gsaRatesLoadedSuccess),
            concatLatestFrom(() => [this.store.select(Selectors.selectGsaRates)]),
            switchMap(([, gsaRates]) => {
                const maxHousing = this._stipendService.getMaxHousing(gsaRates);
                return of(PayBillingActions.setMaxHousing({ maxHousing }));
            })
        );
    });

    calculateMaxStipend$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorApiActions.gsaRatesLoadedSuccess),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectGsaRates),
                this.store.select(Selectors.selectIsAyaHousing)
            ]),
            switchMap(([, gsaRates, isAyaHousing]) => {
                const maxStipends = this._marginBillingService.getMaxStipendFromGsaData(isAyaHousing, gsaRates);
                return of(PayBillingActions.setMaxStipends({ maxStipends }));
            })
        );
    });

    calculateBlendedPayRate$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MarginCalculatorApiActions.facilityLoadedSuccess,
                MarginCalculatorPageActions.setFacility,
                MarginCalculatorApiActions.marginLoadedSuccess,
                PayBillingActions.setMarginPayRate,
                MarginCalculatorPageActions.onMarginPayRateChanged,
                PayBillingActions.setPremiumRate,
                MarginCalculatorPageActions.setShiftHours,
                MarginCalculatorPageActions.setBookingWeeks,
                MarginCalculatorApiActions.getContractLengthWithBookingWeeksLoadedSuccess,
                MarginCalculatorComputationActions.payHoursComputedSuccess,
                MarginCalculatorPageActions.setPayHours,
                MarginCalculatorPageActions.setRegularPayHours,
                MarginCalculatorPageActions.setOvertimePayHours
            ),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectFacilityState),
                this.store.select(Selectors.selectMarginPayRate),
                this.store.select(Selectors.selectPremiumRate),
                this.store.select(Selectors.selectShiftHours)
            ]),
            switchMap(([, facilityState, marginPayRate, premiumRate, shiftHours]) => {
                const blendedPayRate = this._formulaService.calculateBlendedPayRate(
                    facilityState,
                    marginPayRate,
                    premiumRate,
                    shiftHours
                );
                return of(PayBillingActions.setBlendedPayRate({ payRate: blendedPayRate }));
            })
        );
    });

    loadEffectiveDates$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.loadMarginStateInsuranceEffectiveDates),
            concatMap(() =>
                this._marginInsuranceService.getMarginStateInsuranceEffectiveDates().pipe(
                    map((marginStateInsuranceEffectiveDates) =>
                        MarginCalculatorApiActions.insuranceEffectiveDatesLoadedSuccess({
                            marginStateInsuranceEffectiveDates
                        })
                    ),
                    catchError(() =>
                        of(
                            MarginCalculatorApiActions.insuranceEffectiveDatesLoadedFail({
                                message: 'Failed to load insurance effective dates'
                            })
                        )
                    )
                )
            )
        );
    });

    insuranceMedicalAndDentalDetailInfoChange$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MarginCalculatorApiActions.insuranceEffectiveDatesLoadedSuccess,
                MarginCalculatorApiActions.clinicianLoadedSuccess,
                MarginCalculatorPageActions.setEndDate,
                MarginCalculatorApiActions.getContractDatesLoadedSuccess,
                MarginCalculatorApiActions.getContractEndDateLoadedSuccess,
                MarginCalculatorPageActions.setMarginMedicalDays,
                MarginCalculatorPageActions.setMarginMedicalInfo,
                MarginCalculatorPageActions.setMarginMedicalAndDentalInfo,
                MarginCalculatorApiActions.insuranceInfoLoadedSuccess,
                MarginCalculatorPageActions.setIsExtendedBenefits,
                MarginCalculatorPageActions.setInsuranceNeeded,
                MarginCalculatorComputationActions.insuranceNeededComputedSuccess,
                MarginCalculatorPageActions.setIneligibilityDialog,
                MarginCalculatorPageActions.setInsuranceOverrideOption,
                MarginCalculatorApiActions.marginLoadedSuccess,
                MarginCalculatorPageActions.setMargin,
                PipelineMarginActions.pipelineMarginSuccess,
                MarginCalculatorPageActions.setMarginDentalDays,
                MarginCalculatorPageActions.setMarginDental,
                MarginCalculatorPageActions.setMarginMedical,
                MarginCalculatorPageActions.setMarginMedicalDays
            ),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectNewEffectiveDateAsDate),
                this.store.select(Selectors.selectOldEffectiveDateAsDate),
                this.store.select(Selectors.selectStartDateAsDate),
                this.store.select(Selectors.selectEndDateAsDate),
                this.store.select(Selectors.selectSavedMarginMedicalDays),
                this.store.select(Selectors.selectMedicalCost),
                this.store.select(Selectors.selectDaysInGap),
                this.store.select(Selectors.selectNewVersionDailyMedicalCost),
                this.store.select(Selectors.selectIsExtendedBenefits),
                this.store.select(Selectors.selectTotalInsuranceDays),
                this.store.select(Selectors.selectDaysInContract),
                this.store.select(Selectors.selectDaysInGapBeforeNewEffectiveDate),
                this.store.select(Selectors.selectDaysInGapOnOrAfterNewEffectiveDate),
                this.store.select(Selectors.selectMarginDatePipelinedAsDate),
                this.store.select(Selectors.selectReleaseNewEffectiveDateAsDate),
                this.store.select(Selectors.selectNeedInsurance),
                this.store.select(Selectors.selectSavedMarginDentalDays),
                this.store.select(Selectors.selectDentalCost),
                this.store.select(Selectors.selectNewVersionDailyDentalCost)
            ]),
            switchMap(
                ([
                    ,
                    newEffectiveDate,
                    oldEffectiveDate,
                    startDateAsDate,
                    endDateAsDate,
                    medicalDays,
                    medicalCost,
                    daysInGap,
                    newVersionMedicalCost,
                    isExtendedBenefits,
                    totalDays,
                    daysInContract,
                    daysInGapBeforeNewEffectiveDate,
                    daysInGapOnOrAfterNewEffectiveDate,
                    marginDatePipelinedAsDate,
                    releaseNewEffectiveDate,
                    needInsurance,
                    dentalDays,
                    dentalCost,
                    newVersionDentalCost
                ]) => {
                    if (!isEmpty(newEffectiveDate) && !isEmpty(oldEffectiveDate) && !isEmpty(releaseNewEffectiveDate)) {
                        const totalMedicalCostCalculated = MarginCalculatorInsuranceHelperService.getTotalMedicalCost(
                            newEffectiveDate,
                            oldEffectiveDate,
                            startDateAsDate,
                            endDateAsDate,
                            medicalDays,
                            medicalCost,
                            daysInGap,
                            newVersionMedicalCost,
                            isExtendedBenefits,
                            totalDays,
                            daysInContract,
                            daysInGapBeforeNewEffectiveDate,
                            daysInGapOnOrAfterNewEffectiveDate,
                            marginDatePipelinedAsDate,
                            releaseNewEffectiveDate,
                            needInsurance
                        );
                        const totalDentalCostCalculated = MarginCalculatorInsuranceHelperService.getTotalDentalCost(
                            newEffectiveDate,
                            oldEffectiveDate,
                            startDateAsDate,
                            endDateAsDate,
                            dentalDays,
                            dentalCost,
                            daysInGap,
                            newVersionDentalCost,
                            isExtendedBenefits,
                            totalDays,
                            daysInContract,
                            daysInGapBeforeNewEffectiveDate,
                            daysInGapOnOrAfterNewEffectiveDate,
                            marginDatePipelinedAsDate,
                            releaseNewEffectiveDate,
                            needInsurance
                        );

                        return of(
                            MarginCalculatorPageActions.setTotalMedicalAndDentalCostCalculated({
                                totalMedicalCostCalculated,
                                totalDentalCostCalculated
                            })
                        );
                    }

                    return of(
                        MarginCalculatorPageActions.noOp({ debug: 'insuranceMedicalAndDentalDetailInfoChange$ effect' })
                    );
                }
            )
        );
    });

    handleInsuranceSelected$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorApiActions.insuranceInfoLoadedSuccess),
            concatLatestFrom(() => [this.store.select(Selectors.selectTotalInsuranceDays)]),
            switchMap(([, totalDays]) => {
                if (this._marginFormService.parentForm.dirty) {
                    return of(
                        MarginCalculatorPageActions.setMarginMedicalAndDentalInfo({
                            isMedical: true,
                            noOfMedicalDays: totalDays,
                            isDental: true,
                            noOfDentalDays: totalDays
                        })
                    );
                }
                return of(MarginCalculatorPageActions.noOp({ debug: 'handleInsuranceSelected$' }));
            })
        );
    });

    handleMedicalDaysChecked$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.setMarginMedical),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectMarginMedical),
                this.store.select(Selectors.selectTotalInsuranceDays)
            ]),
            switchMap(([, medical, noOfMedicalDays]) => {
                if (this._marginFormService?.parentForm?.dirty) {
                    if (!medical) {
                        noOfMedicalDays = 0;
                    }
                    return of(MarginCalculatorPageActions.setMarginMedicalDays({ noOfMedicalDays }));
                }

                return of(MarginCalculatorPageActions.noOp({ debug: 'handleMedicalDaysChecked$ effect' }));
            })
        );
    });

    handleDentalDaysChecked$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.setMarginDental),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectMarginDental),
                this.store.select(Selectors.selectTotalInsuranceDays)
            ]),
            switchMap(([, dental, noOfDentalDays]) => {
                if (this._marginFormService?.parentForm?.dirty) {
                    if (!dental) {
                        noOfDentalDays = 0;
                    }
                    return of(MarginCalculatorPageActions.setMarginDentalDays({ noOfDentalDays }));
                }

                return of(MarginCalculatorPageActions.noOp({ debug: 'handleDentalDaysChecked$ effect' }));
            })
        );
    });

    showShiftAmount$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MarginCalculatorApiActions.facilityLoadedSuccess,
                MarginCalculatorPageActions.setFacility,
                MarginCalculatorApiActions.jobLoadedSuccess
            ),
            concatLatestFrom(() => [this.store.select(Selectors.selectFacility)]),
            switchMap(([, facility]) => {
                return of(
                    MarginCalculatorPageActions.setShowShiftAmount({
                        showShiftAmt: FacilityHelperService.isSpecialState(facility)
                    })
                );
            })
        );
    });

    setContractFeesFromBillingRates$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorApiActions.billingRatesLoadedSuccess),
            concatLatestFrom(() => [this.store.select(Selectors.selectIsMarginCreatedFromJob)]),
            switchMap(([action, isMarginCreatedFromJob]) => {
                if (this._marginFormService?.parentForm?.dirty || isMarginCreatedFromJob) {
                    const contractFees =
                        action.billingRates && action.billingRates.length > 0
                            ? (action.billingRates[0]?.payBackPercent ?? 0)
                            : 0;
                    return of(PayBillingActions.setContractFees({ contractFees }));
                }
                return of(MarginCalculatorPageActions.noOp({ debug: 'setContractFeesFromBillingRates$ effect' }));
            })
        );
    });

    setDiscountPercentFromFacility$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorApiActions.facilityLoadedSuccess, MarginCalculatorApiActions.jobLoadedSuccess),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectFacilityDiscountPercent),
                this.store.select(Selectors.selectIsMarginCreatedFromJob)
            ]),
            switchMap(([action, discountPercent, isMarginCreatedFromJob]) => {
                if (
                    this._marginFormService?.parentForm?.dirty ||
                    isMarginCreatedFromJob ||
                    action.type === MarginCalculatorApiActions.jobLoadedSuccess.type
                ) {
                    return of(PayBillingActions.earlyPaymentDiscountChanged({ discountPercent }));
                }
                return of(MarginCalculatorPageActions.noOp({ debug: 'setDiscountPercentFromFacility$ effect' }));
            })
        );
    });

    loadHousewareList$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                HousingActions.revealHousingDetailsClicked,
                HousingActions.setHousingSelected,
                MarginCalculatorApiActions.marginLoadedSuccess
            ),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectIsAyaHousing),
                this.store.select(Selectors.selectHousewares)
            ]),
            concatMap(([, isAyaHousing, existingHousewares]) =>
                isAyaHousing && !existingHousewares?.length
                    ? this._marginHousingService.getHousewares().pipe(
                          map((housewares: Houseware[]) => {
                              if (!housewares.some((item) => item.id === HOUSEWARE_OTHER_ID)) {
                                  housewares.push({ id: HOUSEWARE_OTHER_ID, title: '' });
                              }
                              return MarginCalculatorApiActions.housewareListLoadedSuccess({ housewares });
                          }),
                          catchError(() =>
                              of(
                                  MarginCalculatorApiActions.housewareListLoadedFail({
                                      message: 'Failed to load houseware list'
                                  })
                              )
                          )
                      )
                    : of(MarginCalculatorPageActions.noOp({ debug: 'loadHousewareList$ effect' }))
            )
        );
    });

    housingSelected$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(HousingActions.setHousingSelected),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectStartDateAsDate),
                this.store.select(Selectors.selectEndDateAsDate)
            ]),
            switchMap(([housingSelected, startDate, endDate]) => {
                if (housingSelected.housingSelected) {
                    const moveInDate = HousingStateHelpers.getMoveInDate(startDate);
                    const moveOutDate = HousingStateHelpers.getMoveOutDate(endDate);
                    return of(HousingActions.resetHousingFields({ moveInDate, moveOutDate }));
                }
                return of(MarginCalculatorPageActions.noOp({ debug: 'housingSelected$ effect' }));
            })
        );
    });

    getTargetMargin$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorComputationActions.loadTargetMargin),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectFacilityId),
                this.store.select(Selectors.selectJobId),
                this.store.select(Selectors.selectIsExtension)
            ]),
            concatMap(([, facilityId, jobId, isExtension]) =>
                this._marginService.getTargetMargin(facilityId, jobId, isExtension).pipe(
                    map((targetMargin) => MarginCalculatorApiActions.targetMarginLoadedSuccess({ targetMargin })),
                    catchError(() =>
                        of(
                            MarginCalculatorApiActions.targetMarginLoadedFail({
                                message: 'Failed to load Target Margin'
                            })
                        )
                    )
                )
            )
        );
    });

    getTargetMarginWithProfession$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorComputationActions.loadTargetMarginWithProfession),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectFacilityId),
                this.store.select(Selectors.selectProfessionId),
                this.store.select(Selectors.selectIsExtension)
            ]),
            concatMap(([, facilityId, professionId, isExtension]) =>
                this._marginService.getTargetMarginWithProfession(facilityId, professionId, isExtension).pipe(
                    map((targetMargin) =>
                        MarginCalculatorApiActions.targetMarginWithProfessionLoadedSuccess({ targetMargin })
                    ),
                    catchError(() =>
                        of(
                            MarginCalculatorApiActions.targetMarginWithProfessionLoadedFail({
                                message: 'Failed to load Target Margin With Profession'
                            })
                        )
                    )
                )
            )
        );
    });

    getTargetMarginWithExpertise$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorComputationActions.loadTargetMarginWithExpertise),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectFacilityId),
                this.store.select(Selectors.selectProfessionId),
                this.store.select(Selectors.selectSpecialtyId),
                this.store.select(Selectors.selectIsExtension)
            ]),
            concatMap(([, facilityId, professionId, specialtyId, isExtension]) =>
                this._marginService
                    .getTargetMarginWithExpertise(facilityId, professionId, specialtyId, isExtension)
                    .pipe(
                        map((targetMargin) =>
                            MarginCalculatorApiActions.targetMarginWithExpertiseLoadedSuccess({ targetMargin })
                        ),
                        catchError(() =>
                            of(
                                MarginCalculatorApiActions.targetMarginWithExpertiseLoadedFail({
                                    message: 'Failed to load Target Margin With Expertise'
                                })
                            )
                        )
                    )
            )
        );
    });

    targetMargin$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MarginCalculatorApiActions.marginLoadedSuccess,
                MarginCalculatorApiActions.facilityLoadedSuccess,
                MarginCalculatorApiActions.clinicianLoadedSuccess,
                MarginCalculatorPageActions.specialtyIdSelected,
                MarginCalculatorPageActions.setIsExtension,
                MarginCalculatorApiActions.jobLoadedSuccess
            ),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectFacilityId).pipe(startWith(null)),
                this.store.select(Selectors.selectClinicianId).pipe(startWith(null)),
                this.store.select(Selectors.selectProfessionId).pipe(startWith(null)),
                this.store.select(Selectors.selectSpecialtyId).pipe(startWith(null)),
                this.store.select(Selectors.selectJobId).pipe(startWith(0)),
                this.store.select(Selectors.selectTargetMargin).pipe(startWith(null)),
                this.store.select(Selectors.selectIsExtension).pipe(startWith(false))
            ]),
            switchMap(
                ([
                    action,
                    facilityId,
                    clinicianId,
                    professionId,
                    specialtyId,
                    jobId,
                    existingTargetMargin,
                    isExtension
                ]) => {
                    if (
                        existingTargetMargin &&
                        !this._marginFormService.parentForm.get(FORM_NAMES.contract_shift).dirty &&
                        !this._marginFormService.parentForm.get(FORM_NAMES.overview).dirty
                    ) {
                        return of(
                            MarginCalculatorComputationActions.setTargetMargin({
                                targetMargin: existingTargetMargin
                            })
                        );
                    }
                    if (jobId > 0) {
                        return of(
                            MarginCalculatorComputationActions.loadTargetMargin({
                                facilityId,
                                jobId,
                                isExtension
                            })
                        );
                    }
                    if (!jobId && facilityId && !clinicianId) {
                        return of(
                            MarginCalculatorComputationActions.loadTargetMargin({
                                facilityId,
                                isExtension
                            })
                        );
                    }
                    if (!jobId && facilityId && clinicianId && !specialtyId) {
                        return of(
                            MarginCalculatorComputationActions.loadTargetMarginWithProfession({
                                facilityId,
                                professionId,
                                isExtension
                            })
                        );
                    }
                    if (!jobId && facilityId && professionId && specialtyId > 0) {
                        return of(
                            MarginCalculatorComputationActions.loadTargetMarginWithExpertise({
                                facilityId,
                                professionId,
                                specialtyId,
                                isExtension
                            })
                        );
                    }

                    return of(MarginCalculatorPageActions.noOp({ debug: 'targetMargin$ effect' }));
                }
            )
        );
    });

    handleInsuranceSelection$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorApiActions.insuranceInfoLoadedSuccess),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectInsuranceInfo),
                this.store.select(Selectors.selectInsuranceSelectionRecentTrigger)
            ]),
            switchMap(([, insuranceInfo, triggeredAction]) => {
                if (
                    insuranceInfo &&
                    this._marginFormService?.parentForm?.dirty &&
                    triggeredAction !== MarginCalculatorPageActions.setInsuranceNeeded.type
                ) {
                    let isBenefit = false;
                    if (
                        insuranceInfo.insuranceBenefitTypes === (InsuranceBenefitTypes.NewBenefits as number) ||
                        insuranceInfo.insuranceBenefitTypes === (InsuranceBenefitTypes.Extension as number)
                    ) {
                        isBenefit = true;
                    }

                    return of(MarginCalculatorComputationActions.insuranceNeededComputedSuccess({ isBenefit }));
                }

                return of(MarginCalculatorPageActions.noOp({ debug: 'handleInsuranceSelection$ effect' }));
            })
        );
    });

    handleInsuranceExtension$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorApiActions.insuranceInfoLoadedSuccess),
            concatLatestFrom(() => [this.store.select(Selectors.selectInsuranceInfo)]),
            switchMap(([, insuranceInfo]) => {
                if (this._marginFormService.parentForm.dirty) {
                    if (insuranceInfo && insuranceInfo.insuranceBenefitTypes === InsuranceBenefitTypes.Extension) {
                        return of(
                            MarginCalculatorComputationActions.isExtendedBenefitsComputedSuccess({
                                isExtendedBenefits: true
                            })
                        );
                    }
                    return of(
                        MarginCalculatorComputationActions.isExtendedBenefitsComputedSuccess({
                            isExtendedBenefits: false
                        })
                    );
                }
                return of(MarginCalculatorPageActions.noOp({ debug: 'handleInsuranceExtension$ effect' }));
            })
        );
    });

    handleHousingMonthlyRent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.monthlyRentChanged),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectIsAyaHousing),
                this.store.select(Selectors.selectHousingTypeIsApt),
                this.store.select(Selectors.selectHousingTypeIsHotel)
            ]),
            switchMap(([rent, isAyaHousing, isApt, isHotel]) => {
                if (isAyaHousing && isApt) {
                    return of(
                        HousingActions.setMonthlyRent({ extendedStayHotel: 0, monthlyHousing: rent.monthlyRent })
                    );
                } else if (isAyaHousing && isHotel) {
                    return of(
                        HousingActions.setMonthlyRent({ monthlyHousing: 0, extendedStayHotel: rent.monthlyRent })
                    );
                }
                return of(HousingActions.setMonthlyRent({ monthlyHousing: 0, extendedStayHotel: 0 }));
            })
        );
    });

    loadFacilityMinWageAfterFacilityLoaded$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MarginCalculatorApiActions.facilityLoadedSuccess,
                MarginCalculatorApiActions.marginLoadedSuccess,
                MarginCalculatorApiActions.jobLoadedSuccess,
                MarginCalculatorPageActions.setStartDate
            ),
            concatLatestFrom(() => [this._featureManager.isEnabled(FeatureFlag.MarginCalculatorMinimumWage)]),
            switchMap(([, minimumWageFlag]) =>
                minimumWageFlag
                    ? of(MarginCalculatorPageActions.loadFacilityMinimumWage())
                    : of(MarginCalculatorPageActions.noOp({ debug: 'loadFacilityMinWageAfterFacilityLoaded$ effect' }))
            )
        );
    });

    handleMinimumWage$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MarginCalculatorApiActions.facilityLoadedSuccess,
                MarginCalculatorApiActions.marginLoadedSuccess,
                MarginCalculatorApiActions.jobLoadedSuccess,
                MarginCalculatorPageActions.setStartDate,
                MarginCalculatorApiActions.clinicianLoadedSuccess,
                MarginCalculatorPageActions.specialtyIdSelected
            ),
            concatLatestFrom(() => [this._featureManager.isEnabled(FeatureFlag.ExtendedMinimumWage)]),
            switchMap(([, extendedMinimumWage]) =>
                extendedMinimumWage
                    ? of(MarginCalculatorPageActions.loadMinimumWage())
                    : of(MarginCalculatorPageActions.noOp({ debug: 'handleMinimumWage$ effect' }))
            )
        );
    });

    loadFacilityMinimumWage$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.loadFacilityMinimumWage),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectFacilityId),
                this.store.select(Selectors.selectStartDateAsDate),
                this._featureManager.isEnabled(FeatureFlag.ExtendedMinimumWage)
            ]),
            switchMap(([, facilityId, startDate, extendedMinimumWageEnabled]) =>
                facilityId && !extendedMinimumWageEnabled
                    ? this._facilityMinimumWageService.getMinimumWage(facilityId, startDate).pipe(
                          map((facilityMinimumWage) =>
                              facilityMinimumWage
                                  ? MarginCalculatorApiActions.facilityMinimumWageLoadedSuccess({ facilityMinimumWage })
                                  : MarginCalculatorApiActions.facilityMinimumWageLoadedFail({
                                        message: 'Min Wage for Facility not found'
                                    })
                          ),
                          catchError(() =>
                              of(
                                  MarginCalculatorApiActions.facilityMinimumWageLoadedFail({
                                      message: 'Failed to load Facility Minimum Wage'
                                  })
                              )
                          )
                      )
                    : of(MarginCalculatorPageActions.noOp({ debug: 'loadFacilityMinimumWage$ effect' }))
            )
        );
    });

    loadMinimumWageDetails$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.loadMinimumWage),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectFacilityId),
                this.store.select(Selectors.selectStartDateAsDate),
                this.store.select(Selectors.selectProfessionId),
                this.store.select(Selectors.selectSpecialtyId)
            ]),
            switchMap(([, facilityId, startDate, professionId, specialtyId]) =>
                facilityId && specialtyId
                    ? this._minimumWageService.getMinimumWage(facilityId, startDate, professionId, specialtyId).pipe(
                          map((minimumWageDetails) =>
                              minimumWageDetails
                                  ? MarginCalculatorApiActions.minimumWageDetailsLoadedSuccess({ minimumWageDetails })
                                  : MarginCalculatorApiActions.minimumWageDetailsLoadedFail({
                                        message: 'Min Wage Details not found'
                                    })
                          ),
                          catchError(() =>
                              of(
                                  MarginCalculatorApiActions.minimumWageDetailsLoadedFail({
                                      message: 'Failed to load Minimum Wage Details'
                                  })
                              )
                          )
                      )
                    : of(MarginCalculatorPageActions.noOp({ debug: 'loadMinimumWageDetails$ effect' }))
            )
        );
    });

    linkToOfferClicked$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.linkToOfferClicked),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectMarginId),
                this.store.select(Selectors.selectClinicianId)
            ]),
            switchMap(([, marginId, candidateId]) => {
                const dialog = this._dialog.open(MarginOfferLinkDialogComponent, {
                    disableClose: true,
                    panelClass: ['nova__dialog', 'margin-offer-link-dialog'],
                    data: { candidateId, marginId }
                });
                return dialog.afterClosed().pipe(
                    switchMap((result) => {
                        if (result !== null && result === 0) {
                            return of(
                                MarginCalculatorPageActions.setMarginReviewAndSendEnabled({
                                    reviewAndSendEnabled: true
                                })
                            );
                        }
                        return of(MarginCalculatorPageActions.noOp({ debug: 'linkToOfferClicked$ effect' }));
                    })
                );
            })
        );
    });

    setFirstOrientationHoursFromFacility$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MarginCalculatorApiActions.facilityLoadedSuccess,
                MarginCalculatorApiActions.jobLoadedSuccess,
                MarginCalculatorPageActions.setIsExtension
            ),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectIsExtension),
                this.store.select(Selectors.selectFacilityOrientationHours),
                this.store.select(Selectors.selectMarginExists)
            ]),
            switchMap(([, isExtension, facilityOrientationHours, marginExists]) => {
                if (this._marginFormService?.parentForm?.dirty || !marginExists) {
                    const orientationValue = isExtension ? 0 : facilityOrientationHours;
                    return of(MarginCalculatorPageActions.setFirstOrientationHours({ orientationValue }));
                }
                return of(MarginCalculatorPageActions.noOp({ debug: 'setFirstOrientationHoursFromFacility$ effect' }));
            })
        );
    });

    setFirstOrientationAmountFromFacility$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MarginCalculatorApiActions.facilityLoadedSuccess,
                MarginCalculatorApiActions.jobLoadedSuccess,
                MarginCalculatorPageActions.setIsExtension
            ),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectIsExtension),
                this.store.select(Selectors.selectFacilityOrientationRate),
                this.store.select(Selectors.selectMarginExists)
            ]),
            switchMap(([, isExtension, facilityOrientationRate, marginExists]) => {
                if (this._marginFormService?.parentForm?.dirty || !marginExists) {
                    const orientationAmount = isExtension ? 0 : facilityOrientationRate;
                    return of(MarginCalculatorPageActions.setFirstOrientationAmount({ orientationAmount }));
                }
                return of(MarginCalculatorPageActions.noOp({ debug: 'setFirstOrientationAmountFromFacility$ effect' }));
            })
        );
    });

    setNextOrientationHoursFromFacility$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MarginCalculatorApiActions.facilityLoadedSuccess,
                MarginCalculatorApiActions.jobLoadedSuccess,
                MarginCalculatorPageActions.setIsExtension
            ),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectIsExtension),
                this.store.select(Selectors.selectFacilityOrientationHoursAlt),
                this.store.select(Selectors.selectMarginExists)
            ]),
            switchMap(([, isExtension, facilityOrientationHoursAlt, marginExists]) => {
                if (this._marginFormService?.parentForm?.dirty || !marginExists) {
                    const orientationNextValue = isExtension ? 0 : facilityOrientationHoursAlt;
                    return of(MarginCalculatorPageActions.setNextOrientationHours({ orientationNextValue }));
                }
                return of(MarginCalculatorPageActions.noOp({ debug: 'setNextOrientationHoursFromFacility$ effect' }));
            })
        );
    });

    setNextOrientationAmountFromFacility$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MarginCalculatorApiActions.facilityLoadedSuccess,
                MarginCalculatorApiActions.jobLoadedSuccess,
                MarginCalculatorPageActions.setIsExtension
            ),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectIsExtension),
                this.store.select(Selectors.selectFacilityOrientationRateAlt),
                this.store.select(Selectors.selectMarginExists)
            ]),
            switchMap(([, isExtension, facilityOrientationRateAlt, marginExists]) => {
                if (this._marginFormService?.parentForm?.dirty || !marginExists) {
                    const orientationNextAmount = isExtension ? 0 : facilityOrientationRateAlt;
                    return of(MarginCalculatorPageActions.setNextOrientationAmount({ orientationNextAmount }));
                }
                return of(MarginCalculatorPageActions.noOp({ debug: 'setNextOrientationAmountFromFacility$ effect' }));
            })
        );
    });

    setFirstOrientationTypeFromFacility$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorApiActions.facilityLoadedSuccess, MarginCalculatorApiActions.jobLoadedSuccess),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectFacilityOrientationType),
                this.store.select(Selectors.selectMarginExists)
            ]),
            switchMap(([, orientationType, marginExists]) => {
                if (this._marginFormService?.parentForm?.dirty || !marginExists) {
                    return of(MarginCalculatorPageActions.setFirstOrientationType({ orientationType }));
                }

                return of(MarginCalculatorPageActions.noOp({ debug: 'setFirstOrientationTypeFromFacility$ effect' }));
            })
        );
    });

    setNextOrientationTypeFromFacility$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorApiActions.facilityLoadedSuccess, MarginCalculatorApiActions.jobLoadedSuccess),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectFacilityOrientationTypeAlt),
                this.store.select(Selectors.selectMarginExists)
            ]),
            switchMap(([, orientationNextType, marginExists]) => {
                if (this._marginFormService?.parentForm?.dirty || !marginExists) {
                    return of(MarginCalculatorPageActions.setNextOrientationType({ orientationNextType }));
                }

                return of(MarginCalculatorPageActions.noOp({ debug: 'setNextOrientationTypeFromFacility$ effect' }));
            })
        );
    });

    handleLoadBillingRates$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MarginCalculatorApiActions.facilityLoadedSuccess,
                MarginCalculatorApiActions.jobLoadedSuccess,
                MarginCalculatorPageActions.specialtyIdSelected,
                MarginCalculatorApiActions.clinicianLoadedSuccess
            ),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectFacilityId),
                this.store.select(Selectors.selectProfessionId),
                this.store.select(Selectors.selectSpecialtyId),
                this.store.select(Selectors.selectWeeks),
                this.store.select(Selectors.selectRemoveWeeksCheckWhenLoadingBillingRatesFlag),
                this.store.select(Selectors.selectUserInterfaceAnyControlChanged),
                this.store.select(Selectors.selectPreviousProfessionId),
                this.store.select(Selectors.selectFeatureFlags)
            ]),
            switchMap(
                ([
                    action,
                    facilityId,
                    professionId,
                    specialtyId,
                    weeks,
                    removeWeeksCheck,
                    userInterfaceAnyControlChanged,
                    previousProfessionId,
                    featureFlags
                ]) => {
                    if (
                        featureFlags[PathfindersFlags.CandidateChangeNoBillRateReset] === true &&
                        action.type === MarginCalculatorApiActions.clinicianLoadedSuccess.type &&
                        previousProfessionId === professionId &&
                        userInterfaceAnyControlChanged
                    ) {
                        return of(MarginCalculatorPageActions.noOp({ debug: 'handleLoadBillingRates$ effect' }));
                    } else if (
                        facilityId &&
                        professionId &&
                        specialtyId &&
                        (weeks > 0 || removeWeeksCheck) &&
                        Selectors.selectUserInterfaceAnyControlChanged
                    ) {
                        return of(
                            MarginCalculatorPageActions.loadBillingRates({
                                facilityId,
                                professionId,
                                specialtyId,
                                weeks
                            })
                        );
                    }

                    return of(MarginCalculatorPageActions.clearBillingRates());
                }
            )
        );
    });

    setExtDiscountPercentFromFacility$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorApiActions.facilityLoadedSuccess, MarginCalculatorPageActions.setIsExtension),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectFacilityExtDiscountCalculated),
                this.store.select(Selectors.selectIsExtension)
            ]),
            switchMap(([, facilityExtDiscount, isExtension]) => {
                if (this._marginFormService?.parentForm?.dirty) {
                    const extDiscount = isExtension ? facilityExtDiscount : 0;
                    return of(PayBillingActions.extDiscountChanged({ extDiscount }));
                }
                return of(MarginCalculatorPageActions.noOp({ debug: 'setExtDiscountPercentFromFacility$ effect' }));
            })
        );
    });

    getPremiumBillingRate$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MarginCalculatorComputationActions.billingOvertimeEnabledComputedSuccess,
                MarginCalculatorApiActions.facilityLoadedSuccess,
                MarginCalculatorPageActions.baseBillingRateChanged,
                MarginCalculatorPageActions.setShifts,
                PayBillingActions.setOvertimeRate,
                PayBillingActions.overtimeRulesChanged,
                MarginCalculatorPageActions.setMargin,
                MarginCalculatorApiActions.marginLoadedSuccess
            ),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectPremiumBillingRateRequest),
                this.store.select(Selectors.selectBillingOvertimeEnabled),
                this.store.select(Selectors.selectBaseBillingRateValue),
                this.store.select(Selectors.selectMarginExists),
                this.store.select(Selectors.selectLoadedMargin)
            ]),
            concatMap(([, request, isOvertime, baseBillRate, marginExists, loadedMargin]) => {
                const marginPremiumBillRate =
                    marginExists && !this._marginFormService.parentForm.dirty ? loadedMargin?.premiumBillRate : null;
                if (isOvertime || (marginExists && !this._marginFormService.parentForm.dirty)) {
                    return this._marginBillingService.getPremiumBillingRate(request).pipe(
                        map((premiumBillRate) =>
                            MarginCalculatorApiActions.premiumBillingRateLoadedSuccess({
                                premiumBillRate,
                                marginPremiumBillRate: marginPremiumBillRate || premiumBillRate
                            })
                        ),
                        catchError(() =>
                            of(
                                MarginCalculatorApiActions.premiumBillingRateLoadedFail({
                                    message: 'Failed to load premiumBillingRate'
                                })
                            )
                        )
                    );
                }
                return of(
                    MarginCalculatorApiActions.premiumBillingRateLoadedSuccess({
                        premiumBillRate: baseBillRate,
                        marginPremiumBillRate: baseBillRate
                    })
                );
            })
        );
    });

    setPremiumBillingRate$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorApiActions.premiumBillingRateLoadedSuccess),
            switchMap(({ marginPremiumBillRate }) => {
                return of(
                    MarginCalculatorComputationActions.premiumBillingRateComputedSuccess({
                        premiumBillRate: marginPremiumBillRate
                    })
                );
            })
        );
    });

    setAltShiftsAfterRegularShifts$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.setShifts),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectShifts),
                this.store.select(Selectors.selectIsAlternatingShift)
            ]),
            switchMap(([, shifts, isAlternatingShift]) => {
                return isAlternatingShift
                    ? of(
                          MarginCalculatorPageActions.noOp({
                              debug: 'No need to set alternating shift after regular shift'
                          })
                      )
                    : of(MarginCalculatorPageActions.setAltShifts({ shiftsAlt: shifts }));
            })
        );
    });

    handleReviewAndSendFailure$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                ReviewAndSendActions.createContractRequestFail,
                DuplicateMarginActions.duplicateMarginFail,
                PipelineMarginActions.pipelineMarginFail,
                ReviewAndSendActions.reviewAndSendMarginFail,
                UpdateMarginActions.saveMarginFail
            ),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectSaveMarginActionStatusFailed),
                this.store.select(Selectors.selectSaveMarginActionTypeReviewAndSend)
            ]),
            switchMap(([, isFailed, isActionType]) => {
                if (isFailed && isActionType) {
                    return of(
                        MarginCalculatorPageActions.setMarginReviewAndSendEnabled({ reviewAndSendEnabled: false })
                    );
                }
                return of(MarginCalculatorPageActions.noOp({ debug: 'handleReviewAndSendFailure$ effect' }));
            })
        );
    });

    retrieveValidationsOnMarginIdChange$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MarginCalculatorPageActions.setMarginId,
                validationsActions.saveValidationsSuccess,
                MarginCalculatorApiActions.marginLoadedSuccess,
                MarginCalculatorPageActions.setMargin,
                ReviewAndSendActions.reviewAndSendMarginBlockedByViolations,
                MarginCalculatorApiActions.previousMarginIdLoadedSuccess
            ),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectMarginId),
                this.store.select(Selectors.selectMarginExists),
                this.store.select(Selectors.selectPreviousMarginId),
                this.store.select(Selectors.selectMoneyMovementRulesEngineFlag)
            ]),
            switchMap(([, marginId, marginExists, previousMarginId, moneyMovementRulesEngineFlag]) => {
                if (marginExists) {
                    if (moneyMovementRulesEngineFlag) {
                        if (previousMarginId) {
                            return of(
                                MarginCalculatorApiActions.loadMoneyMovementValidations({ marginId, previousMarginId })
                            );
                        }
                    } else {
                        return of(validationsActions.getValidations({ marginId }));
                    }
                }

                return of(MarginCalculatorPageActions.noOp({ debug: 'retrieveValidationsOnMarginIdChange$ effect' }));
            })
        );
    });

    loadMoneyMovementValidations$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorApiActions.loadMoneyMovementValidations),
            concatMap(({ marginId, previousMarginId }) =>
                this._marginService.getMoneyMovementValidations(marginId, previousMarginId).pipe(
                    map((moneyMovementValidations) =>
                        MarginCalculatorApiActions.moneyMovementValidationsLoadedSuccess({ moneyMovementValidations })
                    ),
                    catchError(() =>
                        of(
                            MarginCalculatorApiActions.moneyMovementValidationsLoadedFail({
                                message: 'Failed to load money movement validations'
                            })
                        )
                    )
                )
            )
        );
    });

    premiumMarginCalculation$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MarginCalculatorApiActions.marginLoadedSuccess,
                MarginCalculatorPageActions.setMargin,
                UpdateMarginActions.saveMarginCommonSuccess,
                DuplicateMarginActions.duplicateMargin,
                PayBillingActions.extDiscountChanged,
                MarginCalculatorPageActions.setIsExtension,
                PayBillingActions.setPremiumRate,
                MarginCalculatorPageActions.setPremiumBillRate,
                MarginCalculatorApiActions.facilityLoadedSuccess,
                MarginCalculatorPageActions.setFacility,
                PayBillingActions.setContractFees,
                PayBillingActions.earlyPaymentDiscountChanged,
                MarginCalculatorComputationActions.premiumBillingRateComputedSuccess,
                PayBillingActions.setOvertimeRate
            ),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectMarginExists),
                this.store.select(Selectors.selectMarginExtDiscount),
                this.store.select(Selectors.selectMarginExtDiscountType),
                this.store.select(Selectors.selectIsExtension),
                this.store.select(Selectors.selectPremiumRate),
                this.store.select(Selectors.selectMarginDetailPremiumBillRate),
                this.store.select(Selectors.selectFacilityState),
                this.store.select(Selectors.selectFacilityExtDiscount),
                this.store.select(Selectors.selectFacilityExtDiscountType),
                this.store.select(Selectors.selectContractFees),
                this.store.select(Selectors.selectMarginDiscountPercent),
                this.store.select(Selectors.selectPremiumMargin),
                this._featureManager.isEnabled(FeatureFlag.SavingOTPremiumMargin)
            ]),
            switchMap(
                ([
                    ,
                    marginExists,
                    marginExtDiscount,
                    marginExtDiscountType,
                    extension,
                    premiumRate,
                    premiumBillRate,
                    facilityState,
                    facilityExtDiscount,
                    facilityExtDiscountType,
                    contractFees,
                    earlyPaymentDiscount,
                    currentPremiumMargin,
                    savingOTPremiumMarginFlag
                ]) => {
                    if (
                        this._marginFormService.parentForm.dirty ||
                        !currentPremiumMargin ||
                        !marginExists ||
                        !savingOTPremiumMarginFlag
                    ) {
                        const multiplier = MarginCalculatorPayBillingHelperService.determineMultiplier(facilityState);

                        const extDiscount =
                            marginExists && !this._marginFormService.parentForm?.dirty
                                ? marginExtDiscount
                                : facilityExtDiscount * 100;
                        const extDiscountType =
                            marginExists && !this._marginFormService.parentForm?.dirty
                                ? marginExtDiscountType
                                : facilityExtDiscountType;

                        const adjustedBillRate = PayBillingStateHelpers.calculateAdjustedBillRate(
                            extension,
                            premiumBillRate,
                            contractFees / 100,
                            extDiscount / 100,
                            earlyPaymentDiscount / 100,
                            extDiscountType
                        );

                        let premiumMargin = PayBillingStateHelpers.calculatePremiumMargin(
                            adjustedBillRate,
                            premiumRate,
                            multiplier
                        );
                        premiumMargin = new Big(premiumMargin).round(14, Big.roundHalfUp).toNumber();
                        premiumMargin = isFinite(premiumMargin) ? premiumMargin : 0;
                        return of(MarginCalculatorPageActions.setPremiumMargin({ premiumMargin }));
                    }

                    return of(MarginCalculatorPageActions.noOp({ debug: 'premiumMarginCalculation$ effect' }));
                }
            )
        );
    });

    setInsuranceHousingDetailsDisabled$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                InsuranceActions.revealInsuranceDetailsClicked,
                HousingActions.revealHousingDetailsClicked,
                MarginCalculatorPageActions.setTotalMedicalAndDentalCostCalculated
            ),
            concatLatestFrom(() => [this.store.select(Selectors.selectMarginFormDisabled)]),
            switchMap(([, isFormDisabled]) => {
                if (isFormDisabled) {
                    this._marginFormService.setFormReadonly();
                } else {
                    this._marginFormService.setFormEditable();
                }
                return of(MarginCalculatorPageActions.noOp({ debug: 'setInsuranceHousingDetailsDisabled$ effect' }));
            })
        );
    });

    setContinuedHousingOnSetIsExtension$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.setIsExtension),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectIsAyaHousing),
                this.store.select(Selectors.selectIsExtension)
            ]),
            switchMap(([, isAyaHousing, isExtension]) => {
                if (isAyaHousing) {
                    return of(HousingActions.setContinuedHousing({ continuedHousing: isExtension }));
                }
                return of(MarginCalculatorPageActions.noOp({ debug: 'setContinuedHousingOnSetIsExtension$ effect' }));
            })
        );
    });

    professionForDuplicatedOrPipelinedMarginCheck$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorApiActions.clinicianLoadedSuccess),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectProfessionId),
                this.store.select(Selectors.selectOriginalProfessionId),
                this.store.select(Selectors.selectClinicianId),
                this.store.select(Selectors.selectOriginalClinicianId),
                this.store.select(Selectors.selectIsPipelined),
                this.store.select(Selectors.selectDuplicatedFromMarginId),
                this._featureManager.isEnabled(FeatureFlag.LoadPreviousProfessionFix)
            ]),
            switchMap(
                ([
                    ,
                    professionId,
                    originalProfessionId,
                    clinicianId,
                    originalClinicianId,
                    isPipelined,
                    duplicatedMarginId,
                    flagEnabled
                ]) => {
                    if (
                        flagEnabled &&
                        originalProfessionId &&
                        professionId !== originalProfessionId &&
                        clinicianId === originalClinicianId &&
                        (isPipelined || duplicatedMarginId)
                    ) {
                        return of(
                            MarginCalculatorPageActions.loadProfessionWithSpecialties({
                                professionId: originalProfessionId
                            })
                        );
                    }
                    return of(
                        MarginCalculatorPageActions.noOp({
                            debug: 'professionForDuplicatedOrPipelinedMarginCheck$ effect'
                        })
                    );
                }
            )
        );
    });

    loadProfessionWithSpecialties$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.loadProfessionWithSpecialties),
            concatMap(({ professionId }) =>
                this._marginClinicianService.getProfessionWithSpecialties(professionId).pipe(
                    map((professionWithSpecialties) =>
                        MarginCalculatorApiActions.professionWithSpecialtiesLoadedSuccess({ professionWithSpecialties })
                    ),
                    catchError(() =>
                        of(
                            MarginCalculatorApiActions.professionWithSpecialtiesLoadedFail({
                                message: 'Failed to load profession with specialties'
                            })
                        )
                    )
                )
            )
        );
    });

    selectOriginalSpecialty$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorApiActions.professionWithSpecialtiesLoadedSuccess),
            concatLatestFrom(() => [this.store.select(Selectors.selectOriginalSpecialtyId)]),
            switchMap(([, specialtyId]) => {
                return of(MarginCalculatorPageActions.specialtyIdSelected({ specialtyId }));
            })
        );
    });

    setForReferring$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ReimbursementsActions.setReferralBonus),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectReferralBonus),
                this.store.select(Selectors.selectClinicianName)
            ]),
            switchMap(([, referralBonus, clinicianName]) => {
                if (referralBonus) {
                    return of(ReimbursementsActions.setForReferring({ forReferring: clinicianName }));
                }
                return of(MarginCalculatorPageActions.noOp({ debug: 'setForReferring$ effect' }));
            })
        );
    });

    resetOrientationIndicator$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorApiActions.facilityLoadedSuccess),
            concatLatestFrom(() => [this.store.select(Selectors.selectMarginExists)]),
            switchMap(([action, marginExists]) => {
                if (marginExists && action.facility.orientation.allHoursBillable) {
                    return of(MarginCalculatorPageActions.resetOrientationIndicator());
                }

                return of(MarginCalculatorPageActions.noOp({ debug: 'resetOrientationIndicator$ effect' }));
            })
        );
    });

    setFirstOrientationTypeForSavedMargin$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MarginCalculatorApiActions.facilityLoadedSuccess,
                MarginCalculatorApiActions.facilityContractRulesLoadedSucess,
                MarginCalculatorApiActions.jobLoadedSuccess,
                MarginCalculatorApiActions.marginLoadedSuccess
            ),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectFacilityOrientationType),
                this.store.select(Selectors.selectMarginExists),
                this.store.select(Selectors.selectFirstOrientationType),
                this.store.select(Selectors.selectMarginOrientationAllHoursBillable),
                this.store.select(Selectors.selectFacilityGroupOrientationType),
                this.store.select(Selectors.selectOrientationAmountIndicator)
            ]),
            switchMap(
                ([
                    ,
                    facilityOrientationType,
                    marginExists,
                    marginFirstOrientationType,
                    marginAllHoursBillable,
                    facilityGroupOrientationType,
                    marginOrientationAmountIndicator
                ]) => {
                    if (marginExists && !marginAllHoursBillable && marginFirstOrientationType) {
                        return of(
                            MarginCalculatorPageActions.setFirstOrientationType({
                                orientationType: marginFirstOrientationType
                            })
                        );
                    }

                    if (
                        marginExists &&
                        !marginAllHoursBillable &&
                        !marginFirstOrientationType &&
                        marginOrientationAmountIndicator
                    ) {
                        return of(
                            MarginCalculatorPageActions.setFirstOrientationType({
                                orientationType: marginOrientationAmountIndicator
                            })
                        );
                    }

                    if (
                        marginExists &&
                        !marginAllHoursBillable &&
                        !marginFirstOrientationType &&
                        !marginOrientationAmountIndicator
                    ) {
                        return of(
                            MarginCalculatorPageActions.setFirstOrientationType({
                                orientationType: facilityGroupOrientationType
                            })
                        );
                    }

                    if (marginExists && marginAllHoursBillable) {
                        return of(MarginCalculatorPageActions.resetOrientationIndicator());
                    }

                    return of(
                        MarginCalculatorPageActions.setFirstOrientationType({
                            orientationType: facilityOrientationType
                        })
                    );
                }
            )
        );
    });

    setOrientationAllBillableHoursFromFacility$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorApiActions.facilityLoadedSuccess, MarginCalculatorApiActions.jobLoadedSuccess),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectFacilityOrientation),
                this.store.select(Selectors.selectMarginExists)
            ]),
            switchMap(([, facilityOrientation, marginExists]) => {
                if (this._marginFormService?.parentForm?.dirty || !marginExists) {
                    return of(
                        MarginCalculatorPageActions.setOrientationAllHoursBillable({
                            orientationAllHoursBillable: facilityOrientation.allHoursBillable
                        })
                    );
                }

                return of(
                    MarginCalculatorPageActions.noOp({ debug: 'setOrientationAllBillableHoursFromFacility$ effect' })
                );
            })
        );
    });

    setNextOrientationTypeForSavedMargin$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MarginCalculatorApiActions.facilityLoadedSuccess,
                MarginCalculatorApiActions.facilityContractRulesLoadedSucess,
                MarginCalculatorApiActions.jobLoadedSuccess,
                MarginCalculatorApiActions.marginLoadedSuccess
            ),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectFacilityOrientationTypeAlt),
                this.store.select(Selectors.selectMarginExists),
                this.store.select(Selectors.selectNextOrientationType),
                this.store.select(Selectors.selectMarginOrientationAllHoursBillable),
                this.store.select(Selectors.selectFacilityGroupOrientationNextType),
                this.store.select(Selectors.selectOrientationNextAmountIndicator)
            ]),
            switchMap(
                ([
                    ,
                    orientationNextType,
                    marginExists,
                    marginNextOrientationType,
                    marginAllHoursBillable,
                    facilityGroupOrientationNextType,
                    marginOrientationNextAmountIndicator
                ]) => {
                    if (marginExists && !marginAllHoursBillable && marginNextOrientationType) {
                        return of(
                            MarginCalculatorPageActions.setNextOrientationType({
                                orientationNextType: marginNextOrientationType
                            })
                        );
                    }

                    if (
                        marginExists &&
                        !marginAllHoursBillable &&
                        !marginNextOrientationType &&
                        marginOrientationNextAmountIndicator
                    ) {
                        return of(
                            MarginCalculatorPageActions.setNextOrientationType({
                                orientationNextType: marginOrientationNextAmountIndicator
                            })
                        );
                    }

                    if (
                        marginExists &&
                        !marginAllHoursBillable &&
                        !marginNextOrientationType &&
                        !marginOrientationNextAmountIndicator
                    ) {
                        return of(
                            MarginCalculatorPageActions.setNextOrientationType({
                                orientationNextType: facilityGroupOrientationNextType
                            })
                        );
                    }

                    if (marginExists && marginAllHoursBillable) {
                        return of(MarginCalculatorPageActions.resetOrientationIndicator());
                    }

                    return of(MarginCalculatorPageActions.setNextOrientationType({ orientationNextType }));
                }
            )
        );
    });

    checkForLoadFacilityContractRules$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorApiActions.facilityLoadedSuccess),
            concatMap(({ facility }) => {
                if (facility.contractGroupSystemId) {
                    return of(
                        MarginCalculatorApiActions.loadFacilityContractRules({
                            contractGroupSystemId: facility.contractGroupSystemId
                        })
                    );
                }

                return of(MarginCalculatorPageActions.noOp({ debug: 'checkForLoadFacilityContractRules$ effect' }));
            })
        );
    });

    loadFacilityContractRules$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorApiActions.loadFacilityContractRules),
            concatMap(({ contractGroupSystemId }) => {
                return this._contractStatusService.getContractRules(contractGroupSystemId, true).pipe(
                    map((facilityContractRules) =>
                        MarginCalculatorApiActions.facilityContractRulesLoadedSucess({ facilityContractRules })
                    ),
                    catchError(() =>
                        of(
                            MarginCalculatorApiActions.facilityContractRulesLoadedFail({
                                message: 'Failed to load facility contract rules'
                            })
                        )
                    )
                );
            })
        );
    });

    basePayRateInViolationCheck$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PayBillingActions.setMarginPayRate, MarginCalculatorPageActions.onMarginPayRateChanged),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectMarginId),
                this.store.select(Selectors.selectMinimumWageValue),
                this.store.select(Selectors.selectFacilityMinWageValue),
                this.store.select(Selectors.selectMarginPayRate),
                this.store.select(Selectors.selectFeatureFlags)
            ]),
            switchMap(([, marginCalcId, minimumWage, facilityMinWage, basePayRate, featureFlags]) => {
                if (
                    featureFlags[PathfindersFlags.MarginValidationLogging] === true &&
                    (minimumWage > basePayRate || facilityMinWage > basePayRate)
                ) {
                    const marginValidationLog = {
                        marginValidationRuleId: MarginValidationRule.basePayRateMinWage,
                        marginId: marginCalcId || null,
                        logDetails: [
                            {
                                fieldName: FORM_CONTROL_NAMES.basePayRate,
                                fieldValue: formatNumber(basePayRate, 'en-US', '1.2-2'),
                                expectedValue: formatNumber(minimumWage || facilityMinWage, 'en-US', '1.2-2')
                            } satisfies MarginValidationLogDetail
                        ]
                    } satisfies MarginValidationLog;

                    return of(MarginCalculatorPageActions.saveMarginValidationLog({ marginValidationLog }));
                }

                return of(MarginCalculatorPageActions.noOp({ debug: 'basePayRateInViolationCheck$ effect' }));
            })
        );
    });

    otPayRateInViolationCheck$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PayBillingActions.setPremiumRate),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectMarginId),
                this.store.select(Selectors.selectMinPremiumPayRate),
                this.store.select(Selectors.selectOtPay),
                this.store.select(Selectors.selectFeatureFlags)
            ]),
            switchMap(([, marginCalcId, minimumWage, otPayRate, featureFlags]) => {
                if (featureFlags[PathfindersFlags.MarginValidationLogging] === true && minimumWage > otPayRate) {
                    const marginValidationLog = {
                        marginValidationRuleId: MarginValidationRule.otPayRateMinWage,
                        marginId: marginCalcId || null,
                        logDetails: [
                            {
                                fieldName: FORM_CONTROL_NAMES.otPayRate,
                                fieldValue: formatNumber(otPayRate, 'en-US', '1.2-2'),
                                expectedValue: formatNumber(minimumWage, 'en-US', '1.2-2')
                            } satisfies MarginValidationLogDetail
                        ]
                    } satisfies MarginValidationLog;

                    return of(MarginCalculatorPageActions.saveMarginValidationLog({ marginValidationLog }));
                }

                return of(MarginCalculatorPageActions.noOp({ debug: 'otPayRateInViolationCheck$ effect' }));
            })
        );
    });

    callbackPayRateInViolationCheck$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PayBillingActions.callbackPayRateChanged),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectMarginId),
                this.store.select(Selectors.selectMinCallbackPayRate),
                this.store.select(Selectors.selectCallbackPayRate),
                this.store.select(Selectors.selectFeatureFlags)
            ]),
            switchMap(([, marginCalcId, minimumWage, callbackPayRate, featureFlags]) => {
                if (featureFlags[PathfindersFlags.MarginValidationLogging] === true && minimumWage > callbackPayRate) {
                    const marginValidationLog = {
                        marginValidationRuleId: MarginValidationRule.callbackPayRateMinWage,
                        marginId: marginCalcId || null,
                        logDetails: [
                            {
                                fieldName: FORM_CONTROL_NAMES.callbackPayRate,
                                fieldValue: formatNumber(callbackPayRate, 'en-US', '1.2-2'),
                                expectedValue: formatNumber(minimumWage, 'en-US', '1.2-2')
                            } satisfies MarginValidationLogDetail
                        ]
                    } satisfies MarginValidationLog;

                    return of(MarginCalculatorPageActions.saveMarginValidationLog({ marginValidationLog }));
                }

                return of(MarginCalculatorPageActions.noOp({ debug: 'callbackPayRateInViolationCheck$ effect' }));
            })
        );
    });

    holidayPayRateInViolationCheck$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PayBillingActions.holidayPayRateChanged),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectMarginId),
                this.store.select(Selectors.selectMinHolidayPayRate),
                this.store.select(Selectors.selectHolidayPayRate),
                this.store.select(Selectors.selectFeatureFlags)
            ]),
            switchMap(([, marginCalcId, minimumWage, holidayPayRate, featureFlags]) => {
                if (featureFlags[PathfindersFlags.MarginValidationLogging] === true && minimumWage > holidayPayRate) {
                    const marginValidationLog = {
                        marginValidationRuleId: MarginValidationRule.holidayPayRateMinWage,
                        marginId: marginCalcId || null,
                        logDetails: [
                            {
                                fieldName: FORM_CONTROL_NAMES.holidayPayRate,
                                fieldValue: formatNumber(holidayPayRate, 'en-US', '1.2-2'),
                                expectedValue: formatNumber(minimumWage, 'en-US', '1.2-2')
                            } satisfies MarginValidationLogDetail
                        ]
                    } satisfies MarginValidationLog;

                    return of(MarginCalculatorPageActions.saveMarginValidationLog({ marginValidationLog }));
                }

                return of(MarginCalculatorPageActions.noOp({ debug: 'holidayPayRateInViolationCheck$ effect' }));
            })
        );
    });

    stipendsInViolationCheck$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                PayBillingActions.setCalculatedStipendValues,
                PayBillingActions.setMealStipend,
                PayBillingActions.setHousingStipend
            ),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectMarginId),
                this.store.select(Selectors.selectStipends),
                this.store.select(Selectors.selectFeatureFlags)
            ]),
            switchMap(([action, marginCalcId, stipends, featureFlags]) => {
                if (!stipends) {
                    return of(MarginCalculatorPageActions.noOp({ debug: 'stipendInViolationCheck$ effect' }));
                }
                let totalStipend = stipends.weeklyStipends;
                if (action.type !== PayBillingActions.setCalculatedStipendValues.type) {
                    totalStipend = PayBillingStateHelpers.getTotalStipends(
                        stipends.mealStipend,
                        stipends.housingStipend
                    );
                }
                if (
                    featureFlags[FeatureFlag.MarginValidationLogging] &&
                    featureFlags[FeatureFlag.StipendsSoftPinkValidation] &&
                    (totalStipend > stipends.maxStipends ||
                        stipends.mealStipend > stipends.maxMeals ||
                        stipends.housingStipend > stipends.maxHousing)
                ) {
                    const marginValidationLog = {
                        marginValidationRuleId: MarginValidationRule.softStopOnPinkStipends,
                        marginId: marginCalcId || null,
                        logDetails: [
                            {
                                fieldName: FORM_CONTROL_NAMES.weeklyStipends,
                                fieldValue: formatNumber(totalStipend, 'en-US', '1.2-2'),
                                expectedValue: formatNumber(stipends.maxStipends, 'en-US', '1.2-2')
                            } satisfies MarginValidationLogDetail,
                            {
                                fieldName: FORM_CONTROL_NAMES.weeklyMeals,
                                fieldValue: formatNumber(stipends.mealStipend, 'en-US', '1.2-2'),
                                expectedValue: formatNumber(stipends.maxMeals, 'en-US', '1.2-2')
                            } satisfies MarginValidationLogDetail,
                            {
                                fieldName: FORM_CONTROL_NAMES.weeklyHousing,
                                fieldValue: formatNumber(stipends.housingStipend, 'en-US', '1.2-2'),
                                expectedValue: formatNumber(stipends.maxHousing, 'en-US', '1.2-2')
                            } satisfies MarginValidationLogDetail
                        ]
                    } satisfies MarginValidationLog;

                    return of(MarginCalculatorPageActions.saveMarginValidationLog({ marginValidationLog }));
                }

                return of(MarginCalculatorPageActions.noOp({ debug: 'stipendInViolationCheck$ effect' }));
            })
        );
    });

    contractMaxRTOCheck$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MarginCalculatorPageActions.setIsExtension,
                MarginCalculatorPageActions.setStartDate,
                MarginCalculatorPageActions.setEndDate,
                MarginCalculatorPageActions.setVacationDates
            ),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectMarginValidationLoggingFlag),
                this.store.select(Selectors.selectMaxRtoToolTipForMultiOrSingleExtensions),
                this.store.select(Selectors.selectRTOTooltip),
                this.store.select(Selectors.selectUserId),
                this.store.select(Selectors.selectMarginId),
                this.store.select(Selectors.selectNeedInsurance),
                this.store.select(Selectors.selectIsExtension),
                this.store.select(Selectors.selectVacationDatesAsDates),
                this.store.select(Selectors.selectContinousRtoDays),
                this.store.select(Selectors.selectPreviousContract),
                this.store.select(Selectors.selectPreviousContractVacationDaysAsDate)
            ]),
            switchMap(
                ([
                    ,
                    validationLogFlag,
                    rtoToolTipExtValidationTriggered,
                    rtoToolTipNewContractTriggered,
                    userId,
                    marginCalcId,
                    needInsurance,
                    isExtension,
                    vactionDays,
                    continousRtoDays,
                    previousContract,
                    previousContractVacationDays
                ]) => {
                    if (
                        validationLogFlag &&
                        (rtoToolTipExtValidationTriggered !== '' || rtoToolTipNewContractTriggered !== null)
                    ) {
                        const marginValidationLog = {
                            marginValidationRuleId: MarginValidationRule.maxRto,
                            marginId: marginCalcId || null,
                            logDetails: [
                                {
                                    fieldName: 'userid',
                                    fieldValue: userId?.toString()
                                } satisfies MarginValidationLogDetail,
                                {
                                    fieldName: 'isextension',
                                    fieldValue: isExtension?.toString()
                                } satisfies MarginValidationLogDetail,
                                {
                                    fieldName: 'hasinsurance',
                                    fieldValue: needInsurance?.toString()
                                } satisfies MarginValidationLogDetail,
                                {
                                    fieldName: '#rtodaysrecruitertriedtoadd',
                                    fieldValue: isExtension
                                        ? continousRtoDays?.toString()
                                        : vactionDays?.length.toString(),
                                    expectedValue: needInsurance
                                        ? maxRtoWithInsurance.toString()
                                        : maxRtoWithoutInsurance.toString()
                                } satisfies MarginValidationLogDetail
                            ]
                        } satisfies MarginValidationLog;
                        if (
                            isExtension &&
                            previousContract != null &&
                            previousContract?.vacationDays !== '' &&
                            previousContract?.vacationDays !== null &&
                            previousContractVacationDays !== undefined
                        ) {
                            marginValidationLog.logDetails.push(
                                {
                                    fieldName: 'previouscontractid',
                                    fieldValue: previousContract?.contractId.toString()
                                } satisfies MarginValidationLogDetail,
                                {
                                    fieldName: 'previouscontract#ofvacationdays',
                                    fieldValue: previousContractVacationDays?.length.toString()
                                } satisfies MarginValidationLogDetail
                            );
                        }
                        return of(MarginCalculatorPageActions.saveMarginValidationLog({ marginValidationLog }));
                    }
                    return of(MarginCalculatorPageActions.noOp({ debug: 'contractMaxRTOCheck$ effect' }));
                }
            )
        );
    });

    contractDayOneOrEndNoRTOAlertCheck$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MarginCalculatorPageActions.setStartDate,
                MarginCalculatorPageActions.setEndDate,
                MarginCalculatorPageActions.setVacationDates
            ),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectMarginValidationLoggingFlag),
                this.store.select(Selectors.selectEnableRTOAlertMessage),
                this.store.select(Selectors.selectUserId),
                this.store.select(Selectors.selectMarginId),
                this.store.select(Selectors.selectStartDateAsDate),
                this.store.select(Selectors.selectEndDateAsDate),
                this.store.select(Selectors.selectVacationDaysRequested)
            ]),
            switchMap(
                ([
                    ,
                    validationLogFlag,
                    enableRTOAlertTriggered,
                    userId,
                    marginCalcId,
                    startDate,
                    endDate,
                    vacationDays
                ]) => {
                    if (validationLogFlag && enableRTOAlertTriggered) {
                        const marginValidationLog = {
                            marginValidationRuleId: MarginValidationRule.softStopOnBeginningAndEndRto,
                            marginId: marginCalcId || null,
                            logDetails: [
                                {
                                    fieldName: 'userid',
                                    fieldValue: userId?.toString()
                                } satisfies MarginValidationLogDetail,
                                {
                                    fieldName: 'contractstartdate',
                                    fieldValue: startDate?.toLocaleDateString()
                                } satisfies MarginValidationLogDetail,
                                {
                                    fieldName: 'contractenddate',
                                    fieldValue: endDate?.toLocaleDateString()
                                } satisfies MarginValidationLogDetail,
                                {
                                    fieldName: 'rtodates',
                                    fieldValue: vacationDays
                                } satisfies MarginValidationLogDetail
                            ]
                        } satisfies MarginValidationLog;

                        return of(MarginCalculatorPageActions.saveMarginValidationLog({ marginValidationLog }));
                    }
                    return of(
                        MarginCalculatorPageActions.noOp({ debug: 'contractDayOneOrEndNoRTOAlertCheck$ effect' })
                    );
                }
            )
        );
    });

    saveMarginValidationLog$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.saveMarginValidationLog),
            concatMap(({ marginValidationLog }) => {
                this._marginLogService.postMarginValidationLog(marginValidationLog).subscribe();
                return of(MarginCalculatorPageActions.noOp({ debug: 'saveMarginValidationLog$ effect' }));
            })
        );
    });

    handleRelocationType$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorPageActions.setIsExtension),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectIsExtension),
                this.store.select(Selectors.selectMarginExists)
            ]),
            switchMap(([, isExtension, marginExists]) => {
                if (this._marginFormService?.parentForm?.dirty || !marginExists) {
                    const relocationType = isExtension ? RelocationType.endingRelocation : RelocationType.evenSplit;
                    return of(ReimbursementsActions.setRelocationType({ relocationType }));
                }
                return of(MarginCalculatorPageActions.noOp({ debug: 'handleRelocationType$ effect' }));
            })
        );
    });

    setPreviousMarginId$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorApiActions.marginLoadedSuccess),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectMarginId),
                this.store.select(Selectors.selectUserId)
            ]),
            concatMap(([, marginId, userId]) => {
                if (marginId && userId) {
                    return this._contractRequestService.getChangeType(userId, marginId).pipe(
                        map((changeType) =>
                            MarginCalculatorApiActions.previousMarginIdLoadedSuccess({
                                previousMarginId: changeType.previousMarginId
                            })
                        ),
                        catchError(() =>
                            of(
                                MarginCalculatorApiActions.previousMarginIdLoadedFail({
                                    message: 'Failed to load previous margin id'
                                })
                            )
                        )
                    );
                }

                return of(MarginCalculatorPageActions.noOp({ debug: 'setPreviousMarginId$ effect' }));
            })
        );
    });

    checkForLoadOfferAyaPaidBonus$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorApiActions.marginLoadedSuccess),
            concatLatestFrom(() => [this.store.select(Selectors.selectOfferId)]),
            concatMap(([, offerId]) => {
                if (offerId) {
                    return of(MarginCalculatorApiActions.loadOfferAyaPaidBonusAmount({ offerId }));
                }

                return of(MarginCalculatorPageActions.noOp({ debug: 'loadOfferAyaPaidBonus$ effect' }));
            })
        );
    });

    loadOfferAyaPaidBonus$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorApiActions.loadOfferAyaPaidBonusAmount),
            concatMap(({ offerId }) => {
                return this._offerService.getAyaPaidBonusAmount(offerId).pipe(
                    map((offerAyaPaidBonus) =>
                        MarginCalculatorApiActions.offerAyaPaidBonusLoadedSuccess({ offerAyaPaidBonus })
                    ),
                    catchError(() =>
                        of(
                            MarginCalculatorApiActions.offerAyaPaidBonusLoadedFail({
                                message: 'Failed to load offer aya paid bonus'
                            })
                        )
                    )
                );
            })
        );
    });

    constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        private readonly _featureManager: LDFeatureManager,
        private readonly _marginService: MarginCalculatorMarginService,
        private readonly _contractStatusService: ContractStatusService,
        private readonly _marginBillingService: MarginCalculatorBillingService,
        private readonly _marginCalculatorAuthService: MarginCalculatorAuthService,
        private readonly _marginCalculatorFacilityService: MarginCalculatorFacilityService,
        private readonly _marginClinicianService: MarginCalculatorClinicianService,
        private readonly _marginJobService: MarginCalculatorJobService,
        private readonly _marginCalculatorSpecialtyService: MarginCalculatorSpecialtyService,
        private readonly _marginCalculatorReimbursementService: MarginCalculatorReimbursementService,
        private readonly _marginContractInfoHelperService: MarginCalculatorContractInfoHelperService,
        private readonly _marginDateService: MarginCalculatorDateService,
        private readonly _marginFormService: MarginCalculatorFormService,
        private readonly _marginHousingService: MarginCalculatorHousingService,
        private readonly _marginInsuranceService: MarginCalculatorInsuranceService,
        private readonly _formulaService: MarginCalculatorFormulaService,
        private readonly _stipendService: MarginCalculatorStipendsService,
        private readonly _facilityMinimumWageService: FacilityMinimumWageService,
        private readonly _minimumWageService: MinimumWageService,
        private readonly _marginLogService: MarginCalculatorLogService,
        private readonly _dialog: MatDialog,
        private readonly _confirmDialogService: ConfirmDialogService,
        private readonly _toasterService: ToasterService,
        private readonly _contractRequestService: ContractRequestService,
        private readonly _offerService: SharedOffersService
    ) {}
}
